import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import { Button } from "@/components/ui/button";
import {
  addImageToAdCreative,
  fetchMedia,
  updateEditConfiguration,
} from "../../apiService";
import { runGrabTextFlow, type GrabTextFlowHandle } from "./grabTextFlow";
import DiagonalShimmer from "@/components/common/loaders/DiagonalShimmer";
import { useGlobalStore } from "@/GlobalStore";
import UnsavedChangesDialog from "@/components/common/UnsavedChangesDialog";
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconStrikethrough,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconMinus,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import {
  RiText,
  RiEraserLine,
  RiSparkling2Line,
  RiDownloadLine,
  RiCloseLargeLine,
  RiSaveLine,
  RiImageEditLine,
  RiArrowGoBackFill,
  RiArrowGoForwardFill,
} from "@remixicon/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMasking } from "./useMasking";
import KonvaTextBox, { TextBox } from "./KonvaTextBox";
import KonvaImageBox, { ImageBox as OverlayImageBox } from "./KonvaImageBox";
import FontList from "./FontList";
import AdCard from "@/components/common/adCard";
import { AdCreative, ImageEditHistoryItem } from "../../types";
import MagicEdit from "./MagicEdit";
import CreativeLibraryGallery, {
  type CreativeLibraryItem,
} from "./CreativeLibraryGallery";
import BrandLogoLibrary from "./BrandLogoLibrary";
import type {
  Image,
  GrabTextMetadata,
  TextBox as GrabTextBox,
} from "@/screens/creatives/types";
import { toast } from "sonner";
import useUndoRedo from "@/hooks/useUndoRedo";
import { cn } from "@/lib/utils";
import { getLibraryMedia } from "@/api/globalApiService";

type Props = {
  open: boolean;
  onOpenChange: (
    open: boolean,
    activeImageId?: string,
    updatedCreative?: AdCreative,
  ) => void;
  adCreative: AdCreative | null;
  onEdit?: (
    edit_prompt: string,
    mask_file_id: string | undefined,
    original_file_id: string,
  ) => void;
  onCreativeUpdate?: (updated: AdCreative) => void;
  initialActiveImageId?: string;
};
type SidebarTab =
  | "text"
  | "erase"
  | "magic-generate"
  | "background-ai"
  | "library"
  | null;
const EditImageDialog = ({
  adCreative,
  onOpenChange,
  open,
  onCreativeUpdate,
  initialActiveImageId,
}: Props) => {
  const [activeImageId, setActiveImageId] = useState<string | null>(null);
  const { selectedAccount } = useGlobalStore();
  const [isSaving, setIsSaving] = useState(false);

  // Unsaved changes modal state
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  // Per-image grab state
  type GrabState = {
    isActive: boolean;
    loading: boolean;
    progress: number;
    success: boolean;
    tempImage: Image | null;
    tempMetadata: GrabTextMetadata | null;
    alreadyGrabbed?: boolean;
  };
  const [grabStatesByImageId, setGrabStatesByImageId] = useState<
    Record<string, GrabState>
  >({});

  // Keep a polled copy of media list so UI updates as status changes
  const [polledMediaMap, setPolledMediaMap] = useState<Record<string, Image>>({});
  const pollTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pollingInFlightRef = useRef<Set<string>>(new Set());
  const isPollingEnabledRef = useRef<boolean>(true);
  // Locally appended media (e.g., newly requested aspect ratios)
  const [appendedMedia, setAppendedMedia] = useState<Image[]>([]);
  // Track session-added ids (mirror of ref for convenience; not strictly required for logic)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sessionAddedIdsState, setSessionAddedIdsState] = useState<Set<string>>(
    new Set(),
  );
  const pollControllersRef = useRef<Map<string, AbortController>>(new Map());
  // Track ids that have had a polling loop scheduled to avoid duplicate starts
  const scheduledPollIdsRef = useRef<Set<string>>(new Set());
  // Suppress polling for specific media ids when undone; re-enable on redo
  const suppressedPollingIdsRef = useRef<Set<string>>(new Set());
  // Track media ids that were added during this session (eligible for delete)
  const sessionAddedIdsRef = useRef<Set<string>>(new Set());
  // Track ids explicitly removed via the remove button in this session
  const explicitRemovedImagesRef = useRef<Image[]>([]);

  // Cache of composed data URLs for images previously active (pixel-identical to active renderer)
  const composedImageCacheRef = useRef<Map<string, string>>(new Map());

  // Derived media list from adCreative
  const mediaList = useMemo<Image[]>(
    () => (adCreative?.creative?.images as unknown as Image[]) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );
  const adCreativeId = adCreative?.id;
  // Always reference the latest adCreative to avoid stale closures in async callbacks
  const currentAdCreativeRef = useRef<AdCreative | null>(adCreative);
  useEffect(() => {
    currentAdCreativeRef.current = adCreative;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Snapshot of aspect ratios that existed when dialog opened; used to decide replace vs append
  const initialAspectRatiosRef = useRef<Set<string>>(new Set());
  // Track if we've already performed one replacement for a given ratio in this session
  const replacedInitialRatioRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const ratios = new Set<string>();
    for (const m of (adCreative?.creative?.images as unknown as Image[]) || []) {
      if (m?.aspect_ratio) ratios.add(String(m.aspect_ratio));
    }
    initialAspectRatiosRef.current = ratios;
    replacedInitialRatioRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Seed map from incoming media list and keep order stable
  useEffect(() => {
    const next: Record<string, Image> = {};
    for (const m of mediaList || []) next[m.id] = m;
    // Always sync the polledMediaMap with the latest mediaList data
    // This ensures grab_text_metadata from adCreative is properly reflected
    setPolledMediaMap((prev) => {
      const updated = { ...prev };
      // For each image in mediaList, update polledMediaMap with the latest data
      for (const m of mediaList || []) {
        const existing = updated[m.id];
        if (existing) {
          // Merge with existing data but prioritize mediaList data for core properties
          updated[m.id] = { ...existing, ...m };
        } else {
          updated[m.id] = m;
        }
      }
      return updated;
    });
  }, [mediaList]);

  // Merge media from server with locally appended placeholders/details without duplicates by id
  const baseMediaList = useMemo(() => {
    const merged: Image[] = [...(mediaList || [])];
    const existingIds = new Set(merged.map((m) => m.id));
    for (const m of appendedMedia) {
      if (!existingIds.has(m.id)) merged.push(m);
    }
    for (const m of explicitRemovedImagesRef.current) {
      if (!existingIds.has(m.id)) merged.push(m);
    }
    return merged;
  }, [mediaList, appendedMedia]);
  const polledMediaList = useMemo(
    (): Image[] => baseMediaList.map((m) => polledMediaMap[m.id] || m),
    [baseMediaList, polledMediaMap],
  );
  // Editor-visible ordering and filtering of images (tracked for undo/redo)
  const [visibleImageIds, setVisibleImageIds] = useState<string[]>(() =>
    (baseMediaList || []).map((m) => String(m.id)),
  );
  // Keep a ref of latest visible ids for difference calculations in history apply
  const visibleIdsRef = useRef<string[]>([]);
  useEffect(() => {
    visibleIdsRef.current = (visibleImageIds || []).map((v) => String(v));
  }, [visibleImageIds]);
  // Initialize/merge visible ids with current media ids while preserving order
  useEffect(() => {
    const imageIds = adCreative?.creative?.images?.map((m) => String(m.id)) || [];
    setVisibleImageIds(imageIds);
    // Set active image to first image if not set
    if (imageIds.length > 0 && !activeImageId) {
      setActiveImageId(initialActiveImageId ?? imageIds[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const editorMediaList = useMemo((): Image[] => {
    const map = new Map<string, Image>();
    for (const im of polledMediaList || []) map.set(String(im.id), im);
    const ordered: Image[] = [];
    for (const id of visibleImageIds || []) {
      const im = map.get(String(id));
      if (im) ordered.push(im);
    }
    return ordered;
  }, [polledMediaList, visibleImageIds]);
  // New sidebar state
  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>("text");
  const [sidebarTabByImageId, setSidebarTabByImageId] = useState<
    Record<string, SidebarTab>
  >({});

  // Magic Edit sub-tab state (within "text" tab)
  const [isMagicEditActive, setIsMagicEditActive] = useState(false);

  // Magic edit processing state (separate from media status to avoid image reload)
  const [magicEditProcessingByImageId, setMagicEditProcessingByImageId] = useState<
    Record<string, boolean>
  >({});

  // Magic edit state per image (prompt, reference selection, polling state, history)
  const [magicEditStateByImageId, setMagicEditStateByImageId] = useState<
    Record<
      string,
      {
        prompt: string;
        selectedReferenceImageId: string | null;
        pollingImageId: string | null;
        isProcessing: boolean;
        baseImageId: string | null; // Track which base image this magic edit belongs to
        history: ImageEditHistoryItem[]; // Magic edit history for this image
        isLoadingHistory: boolean; // Loading state for history
        isLoadingMore: boolean; // Loading state for loading more items
        nextCursor: string | null; // Cursor for pagination
        hasMore: boolean; // Whether more items are available
      }
    >
  >({});

  // Track current background_image_edit per image (the background image edit from magic edit history)
  const [currentBackgroundImageEditByImageId, setCurrentBackgroundImageEditByImageId] =
    useState<Record<string, ImageEditHistoryItem | null>>({});

  const baseActiveImage = editorMediaList?.find((img) => img.id === activeImageId);

  // Helper to check if current image is processing (pending or magic edit processing)
  const isCurrentImageProcessing = useMemo(() => {
    const currentImage = polledMediaList?.find((img) => img.id === activeImageId);
    const imageId = currentImage?.id;
    return Boolean(
      currentImage?.status === "pending" ||
        (imageId && magicEditProcessingByImageId[imageId]),
    );
  }, [polledMediaList, activeImageId, magicEditProcessingByImageId]);

  const activeImage = useMemo(() => {
    const baseImage = baseActiveImage;
    if (!baseImage) return baseImage;
    // If we already replaced the creative image at this slot, prefer the updated one from polled map
    const maybeReplaced = polledMediaMap[baseImage.id] || baseImage;
    const s = grabStatesByImageId[baseImage.id];
    // Show tempImage as soon as it's available (during loading or after success)
    if (s?.tempImage) return s.tempImage;
    return maybeReplaced;
  }, [baseActiveImage, grabStatesByImageId, polledMediaMap]);
  // Restore per-image sidebar tab when switching images
  useEffect(() => {
    const id = baseActiveImage?.id;
    if (!id) return;
    const persisted = sidebarTabByImageId[id];

    setActiveSidebarTab(persisted ?? "text");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseActiveImage?.id]);

  const imageUrl = useMemo(() => {
    const baseId = baseActiveImage?.id ?? "";
    const s = grabStatesByImageId[baseId];
    const current = editorMediaList?.find((img) => img.id === activeImageId);

    // If we have a tempImage from grab text operation, use its URL
    if (s?.tempImage) {
      return s.tempImage.permalink_url || s.tempImage.url;
    }

    const clientUrl: string | undefined =
      current?.client_grabbed_image?.permalink_url || current?.client_grabbed_image?.url;
    if (clientUrl) return clientUrl;

    // Always prioritize background_image_edit permalink_url for display
    const backgroundImageEdit = currentBackgroundImageEditByImageId[baseId];
    if (backgroundImageEdit?.permalink_url) {
      return backgroundImageEdit.permalink_url;
    }

    return activeImage?.permalink_url || activeImage?.url;
  }, [
    activeImage,
    baseActiveImage?.id,
    grabStatesByImageId,
    activeImageId,
    editorMediaList,
    currentBackgroundImageEditByImageId,
  ]);

  // Smooth image switching: preload next URL and only then swap into the <img>
  const [displayedUrl, setDisplayedUrl] = useState<string>("");

  useEffect(() => {
    if (!imageUrl) {
      return;
    }
    if (!loadedImageUrlsRef.current.has(imageUrl)) {
      setIsImageReady(false);
    }
    setDisplayedUrl(imageUrl);
    return undefined;
  }, [imageUrl]);

  const {
    isMaskingEnabled,
    setIsMaskingEnabled,
    imgSize,
    setImgSize,
    canvasRef,
    handlePointerDown,
    handlePointerMove,
    finishStroke,
  } = useMasking();

  // Image refs map - separate ref for each image to avoid race conditions
  const imgRefs = useRef<Map<string, HTMLImageElement>>(new Map());
  // Track loaded image URLs to avoid flicker for cached/seen images
  const loadedImageUrlsRef = useRef<Set<string>>(new Set());

  // Helper to get the current image ref based on activeImageId
  const getCurrentImgRef = useCallback(() => {
    if (!activeImage?.id) return null;
    return imgRefs.current.get(activeImage.id) || null;
  }, [activeImage]);

  // Helper to set image ref for a specific image
  const setImgRef = useCallback((imageId: string, element: HTMLImageElement | null) => {
    if (element) {
      imgRefs.current.set(imageId, element);
    } else {
      imgRefs.current.delete(imageId);
    }
  }, []);

  // Create a dynamic ref object for KonvaTextBox compatibility
  const currentImgRefObject = useMemo(
    () => ({
      get current() {
        return getCurrentImgRef();
      },
    }),
    [getCurrentImgRef],
  );

  // Text box state
  const [isTextBoxMode, setIsTextBoxMode] = useState(false);
  const [addNowToken, setAddNowToken] = useState(0);
  // Image overlay boxes state
  const [imageEditConfigs, setImageEditConfigs] = useState<
    Record<string, OverlayImageBox[]>
  >({});
  const [imageDeselectToken, setImageDeselectToken] = useState(0);
  const imageFileInputRef = useRef<HTMLInputElement | null>(null);
  const [nextAddImageToken, setNextAddImageToken] = useState(0);
  const [imageAddRequest, setImageAddRequest] = useState<{
    token: number;
    src: string;
    naturalWidth: number;
    naturalHeight: number;
  } | null>(null);
  // Creative library items
  const [creativeLibrary, setCreativeLibrary] = useState<CreativeLibraryItem[] | null>(
    null,
  );
  const [isLoadingCreativeLibrary, setIsLoadingCreativeLibrary] = useState(false);

  // Fetch creative library items on mount/account change
  useEffect(() => {
    const accountId = String(useGlobalStore.getState().selectedAccount?.id || "");
    if (!accountId) return;
    if (creativeLibrary !== null || isLoadingCreativeLibrary) return;
    setIsLoadingCreativeLibrary(true);
    getLibraryMedia(accountId)
      .then((response) => {
        setCreativeLibrary(
          (response.data || []).map((item) => ({
            id: item.id,
            file_url: item.file_url,
          })),
        );
      })
      .catch(() => setCreativeLibrary([]))
      .finally(() => setIsLoadingCreativeLibrary(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id, isLoadingCreativeLibrary]);

  // Deprecated global grab text state (replaced by per-image state)

  // const grabTextProgressRef = useRef<ReturnType<typeof setInterval> | null>(null); // Removed unused variable

  // Toolbar state: font and color sheets
  // Left overlay panel content: 'font' | 'color' | null
  const [leftPanel, setLeftPanel] = useState<null | "font" | "color">(null);
  const [selectedFont, setSelectedFont] = useState<string>("Lato");
  const [fontSize, setFontSize] = useState<number>(36);
  const [selectedColor, setSelectedColor] = useState<string>("#000000");
  const [customColorValue, setCustomColorValue] = useState<string>("#000000");
  // Font size input and dropdown options
  const [fontSizeInput, setFontSizeInput] = useState<string>(
    String(Math.round(fontSize)),
  );
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const fontDropdownRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setFontSizeInput(String(Math.round(fontSize)));
  }, [fontSize]);
  // Close custom dropdown on outside click
  useEffect(() => {
    if (!isFontDropdownOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!fontDropdownRef.current || !target) return;
      if (!fontDropdownRef.current.contains(target)) setIsFontDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isFontDropdownOpen]);
  const fontSizeOptions = useMemo(
    () =>
      [
        6, 8, 10, 12, 14, 16, 18, 21, 24, 28, 32, 36, 42, 48, 56, 64, 72, 80, 88, 96, 104,
        120, 144,
      ].map((n) => ({ key: String(n), label: String(n), value: n })),
    [],
  );
  // Font availability detection (kept but Google Fonts is the primary source)
  const ALL_FONTS = useMemo(
    () => [
      "Montserrat",
      "Inter",
      "Roboto",
      "Open Sans",
      "Lato",
      "Poppins",
      "Source Sans Pro",
      "Noto Sans",
      "Nunito",
      "Merriweather",
      "Playfair Display",
      "Oswald",
      "Raleway",
      "Work Sans",
      "Rubik",
      "Ubuntu",
      "Fira Sans",
      "PT Sans",
      "DM Sans",
      "Manrope",
      "Karla",
      "Cabin",
      "Mulish",
      "Quicksand",
      "IBM Plex Sans",
      "Space Grotesk",
      "Arial",
      "Helvetica Neue",
      "Segoe UI",
      "System UI",
      "Apple System",
      "Georgia",
      "Times New Roman",
      "Courier New",
    ],
    [],
  );
  // Deprecated local font detection (Google Fonts only now)
  // Placeholder arrays kept for forward-compatibility
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const availableFonts: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unavailableFonts: string[] = [];
  // Google Fonts dynamic list
  const [googleFonts, setGoogleFonts] = useState<string[] | null>(null);
  const [isLoadingGoogleFonts, setIsLoadingGoogleFonts] = useState(false);
  const [fontSearch, setFontSearch] = useState("");
  // Google-only source
  const filteredGoogleFonts = useMemo(() => {
    const source = googleFonts || [];
    const q = fontSearch.trim().toLowerCase();
    const list = q ? source.filter((f) => f.toLowerCase().includes(q)) : [...source];
    list.sort((a, b) => a.localeCompare(b));
    return list;
  }, [googleFonts, fontSearch]);
  // Lazy pagination and per-item load state
  const PAGE_SIZE = 40;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const POPULAR_FONTS = useMemo(
    () => [
      "Poppins",
      "Inter",
      "Montserrat",
      "Roboto",
      "Open Sans",
      "Lato",
      "Nunito",
      "Work Sans",
      "Raleway",
      "Playfair Display",
      "Merriweather",
      "Source Sans Pro",
    ],
    [],
  );
  // Split into popular (kept at top if present) and the rest
  const popularFonts = useMemo(() => {
    const list = POPULAR_FONTS.filter((f) => filteredGoogleFonts.includes(f));
    list.sort((a, b) => a.localeCompare(b));
    return list;
  }, [POPULAR_FONTS, filteredGoogleFonts]);
  const remainingFonts = useMemo(
    () => filteredGoogleFonts.filter((f) => !POPULAR_FONTS.includes(f)),
    [filteredGoogleFonts, POPULAR_FONTS],
  );
  const displayedFonts = useMemo(
    () => remainingFonts.slice(0, visibleCount),
    [remainingFonts, visibleCount],
  );
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const [loadingFonts, setLoadingFonts] = useState<Set<string>>(new Set());
  // No-op effect retained for future use; Google Fonts is the only source now
  useEffect(() => {
    void ALL_FONTS;
  }, [ALL_FONTS]);

  // Fetch Google Fonts list when text tab is active
  useEffect(() => {
    const shouldFetch =
      activeSidebarTab === "text" && googleFonts === null && !isLoadingGoogleFonts;
    if (!shouldFetch) return;
    // Vite exposes import.meta.env at build-time
    const metaEnv = (import.meta as { env?: Record<string, string> }).env;
    const key = metaEnv?.VITE_GOOGLE_FONTS_API_KEY;
    if (!key) {
      // Without API key, keep using local list
      setGoogleFonts([]);
      return;
    }
    setIsLoadingGoogleFonts(true);
    fetch(
      `https://www.googleapis.com/webfonts/v1/webfonts?key=${encodeURIComponent(key)}&sort=popularity`,
    )
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to fetch Google Fonts");
        const data = (await res.json()) as { items?: Array<{ family: string }> };
        const families = (data.items || []).map((it) => it.family).filter(Boolean);
        setGoogleFonts(families);
      })
      .catch(() => setGoogleFonts([]))
      .finally(() => setIsLoadingGoogleFonts(false));
  }, [activeSidebarTab, googleFonts, isLoadingGoogleFonts]);

  // Ensure selected webfont stylesheet is present
  const ensureGoogleFontLoaded = useCallback((family: string) => {
    const id = `gf-${family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    const encoded = family.replace(/ /g, "+");
    // Request a common set of weights to maximize compatibility across non-variable families
    // Many families (e.g., Poppins) are not variable; load discrete weights instead
    link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, []);

  // Load an individual font face and mark as loaded
  const requestFontFace = useCallback(
    (family: string) => {
      if (loadedFonts.has(family) || loadingFonts.has(family)) return;
      setLoadingFonts((prev) => new Set(prev).add(family));
      ensureGoogleFontLoaded(family);
      const quoted = family.includes(" ") ? `"${family}"` : family;
      const doMarkLoaded = () => {
        setLoadedFonts((prev) => new Set(prev).add(family));
        setLoadingFonts((prev) => {
          const next = new Set(prev);
          next.delete(family);
          return next;
        });
      };
      try {
        if (document.fonts && document.fonts.load) {
          // Request a common size; resolves when face is available
          void document.fonts.load(`16px ${quoted}`).then(() => doMarkLoaded());
        } else {
          // Fallback: mark loaded after a small delay
          setTimeout(doMarkLoaded, 300);
        }
      } catch {
        setTimeout(doMarkLoaded, 300);
      }
    },
    [ensureGoogleFontLoaded, loadedFonts, loadingFonts],
  );

  // Measure optimal font size (px) for Lato to fit within a display box
  const measureOptimalLatoFontSize = (
    text: string,
    width: number,
    height: number,
    fontWeight: "normal" | "bold",
    fontStyle: string | undefined,
  ): number => {
    let fontSize = Math.min(height * 0.5, width * 0.1);
    const maxFontSize = height * 0.8;
    const minFontSize = 8;
    const measureElement = document.createElement("div");
    measureElement.style.position = "absolute";
    measureElement.style.visibility = "hidden";
    measureElement.style.whiteSpace = "pre-wrap";
    measureElement.style.wordBreak = "break-word";
    measureElement.style.lineHeight = "1.2";
    measureElement.style.fontFamily = "Lato";
    measureElement.style.fontWeight = fontWeight;
    measureElement.style.fontStyle = fontStyle || "normal";
    measureElement.style.width = `${Math.max(1, width - 4)}px`;
    measureElement.style.padding = "2px";
    measureElement.textContent = text || "M";
    document.body.appendChild(measureElement);
    let low = minFontSize;
    let high = maxFontSize;
    let bestFit = minFontSize;
    while (low <= high) {
      fontSize = (low + high) / 2;
      measureElement.style.fontSize = `${fontSize}px`;
      const rect = measureElement.getBoundingClientRect();
      const textHeight = rect.height;
      if (textHeight <= height - 4) {
        bestFit = fontSize;
        low = fontSize + 0.5;
      } else {
        high = fontSize - 0.5;
      }
    }
    document.body.removeChild(measureElement);
    return Math.max(minFontSize, Math.min(maxFontSize, bestFit));
  };

  // Encode a canvas to a compact Data URL without compromising visible quality
  // Tries WebP (q=0.92) and PNG, then returns the smaller
  const encodeCanvasToDataURL = useCallback((canvas: HTMLCanvasElement): string => {
    try {
      // Validate canvas dimensions
      if (canvas.width === 0 || canvas.height === 0) {
        console.warn("Canvas has zero dimensions:", {
          width: canvas.width,
          height: canvas.height,
        });
        return "";
      }

      let png: string | null = null;
      let webp: string | null = null;
      let isTainted = false;

      try {
        png = canvas.toDataURL("image/png");
        if (!png || png === "data:,") {
          console.warn("PNG encoding failed or returned empty data URL");
          png = null;
        }
      } catch (pngError) {
        if (pngError instanceof Error && pngError.name === "SecurityError") {
          console.warn("Canvas is tainted - cannot export as data URL");
          isTainted = true;
        } else {
          console.warn("PNG encoding error:", pngError);
        }
        png = null;
      }

      if (!isTainted) {
        try {
          webp = canvas.toDataURL("image/webp", 0.92);
          if (!webp || webp === "data:,") {
            console.warn("WebP encoding failed or returned empty data URL");
            webp = null;
          }
        } catch (webpError) {
          if (webpError instanceof Error && webpError.name === "SecurityError") {
            console.warn("Canvas is tainted - cannot export as data URL");
            isTainted = true;
          } else {
            console.warn("WebP encoding error:", webpError);
          }
          webp = null;
        }
      }

      // If canvas is tainted, we need to handle this differently
      if (isTainted) {
        console.warn(
          "Canvas is tainted due to cross-origin images. Cannot export as data URL.",
        );
        // Return empty string to indicate failure
        return "";
      }

      if (webp && webp.startsWith("data:image/webp")) {
        if (!png) return webp;
        // Compare base64 lengths; shorter is chosen
        return webp.length < png.length ? webp : png;
      }

      const fallback = png || canvas.toDataURL();
      if (!fallback || fallback === "data:,") {
        console.warn("All encoding methods failed, canvas may be empty");
        return "";
      }

      return fallback;
    } catch (error) {
      console.warn("Canvas encoding error:", error);
      return "";
    }
  }, []);

  // Render an ImageEditState to a PNG data URL at natural resolution (accepts v2 normalized boxes)
  const renderImageEditStateToDataURL = useCallback(
    async (
      im?: {
        aspect_ratio?: string;
        width?: number;
        height?: number;
        text_boxes: Array<
          | (TextBoxPersistedV2 & {
              x?: never;
              y?: never;
              width?: never;
              fontSize?: never;
            })
          | ({ x: number; y: number; width: number; fontSize: number } & Omit<
              TextBoxPersistedV2,
              "x_norm" | "y_norm" | "width_norm" | "font_size_norm"
            >)
        >;
        image_overlays?: Array<{
          src: string;
          x_norm: number;
          y_norm: number;
          width_norm: number;
          height_norm: number;
          rotation?: number;
        }>;
      },
      currentUrl?: string,
    ): Promise<string | null> => {
      try {
        const srcUrl = currentUrl || "";
        if (!srcUrl) return null;
        const baseImg = await loadImageWithCorsFallback(srcUrl);
        const naturalW = Math.max(1, baseImg.naturalWidth || im?.width || 0);
        const naturalH = Math.max(1, baseImg.naturalHeight || im?.height || 0);
        if (!naturalW || !naturalH) return null;
        const canvas = document.createElement("canvas");
        canvas.width = naturalW;
        canvas.height = naturalH;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.imageSmoothingEnabled = true;
        try {
          (
            ctx as CanvasRenderingContext2D & { imageSmoothingQuality?: string }
          ).imageSmoothingQuality = "high";
        } catch {
          // Ignore if imageSmoothingQuality is not supported
        }
        ctx.drawImage(baseImg, 0, 0, naturalW, naturalH);
        // Draw image overlays above base (below text)
        if (im?.image_overlays && im.image_overlays.length) {
          for (const ov of im.image_overlays) {
            try {
              const img = await loadImageWithCorsFallback(ov.src || "");
              const xPx = (ov.x_norm || 0) * naturalW;
              const yPx = (ov.y_norm || 0) * naturalH;
              const wPx = Math.max(1, (ov.width_norm || 0) * naturalW);
              const hPx = Math.max(1, (ov.height_norm || 0) * naturalH);
              const iw = img.naturalWidth || 1;
              const ih = img.naturalHeight || 1;
              // object-fit: contain
              const scaleContain = Math.min(wPx / iw, hPx / ih);
              const rw = iw * scaleContain;
              const rh = ih * scaleContain;
              const dx = (wPx - rw) / 2;
              const dy = (hPx - rh) / 2;
              ctx.save();
              ctx.translate(Math.round(xPx + wPx / 2), Math.round(yPx + hPx / 2));
              if (ov.rotation) ctx.rotate(((ov.rotation || 0) * Math.PI) / 180);
              ctx.translate(-wPx / 2, -hPx / 2);
              ctx.beginPath();
              ctx.rect(0, 0, wPx, hPx);
              ctx.clip();
              ctx.drawImage(img, dx, dy, rw, rh);
              ctx.restore();
            } catch {
              // Ignore overlay image loading errors
            }
          }
        }

        for (const tbRaw of im?.text_boxes || []) {
          const isV2 = "x_norm" in tbRaw && tbRaw.x_norm != null;
          const tb = tbRaw;
          const xPx = isV2
            ? ((tb as TextBoxPersistedV2).x_norm || 0) * naturalW
            : (tb as TextBox).x || 0;
          const yPx = isV2
            ? ((tb as TextBoxPersistedV2).y_norm || 0) * naturalH
            : (tb as TextBox).y || 0;
          const widthPx = isV2
            ? ((tb as TextBoxPersistedV2).width_norm || 0) * naturalW
            : (tb as TextBox).width || 0;
          const fontPx = Math.max(
            8,
            isV2
              ? ((tb as TextBoxPersistedV2).font_size_norm || 0) * naturalH
              : (tb as TextBox).fontSize || 14,
          );
          const fontWeight = tb.isBold ? "700" : "400";
          const fontStyle = tb.isItalic ? "italic" : "normal";
          const fontFamily = tb.fontFamily || "Lato, sans-serif";
          const lineHeight = fontPx * 1.2;
          ctx.font = `${fontStyle} ${fontWeight} ${fontPx}px ${fontFamily}`;
          ctx.fillStyle = tb.fill || "#000000";
          // Align with active renderer
          ctx.textBaseline = "alphabetic";
          const maxW = Math.max(10, widthPx || 200);
          const words = String(tb.text || "").split(/(\s+)/);
          // Build lines with long-token splitting
          const lines: string[] = [];
          let build = "";
          const pushLine = () => {
            if (build.length) {
              lines.push(build.replace(/\s+$/, ""));
              build = "";
            }
          };
          for (const token of words) {
            if (!/\s+/.test(token)) {
              const test = build + token;
              if (ctx.measureText(test).width <= maxW || build === "") {
                if (ctx.measureText(test).width <= maxW) {
                  build = test;
                  continue;
                }
              }
              if (build) pushLine();
              let chunk = "";
              for (const ch of token) {
                const probe = chunk + ch;
                if (ctx.measureText(probe).width > maxW && chunk) {
                  lines.push(chunk);
                  chunk = ch;
                } else {
                  chunk = probe;
                }
              }
              if (chunk) {
                build = chunk + " ";
                continue;
              }
            }
            const candidate = build + token;
            if (ctx.measureText(candidate).width > maxW && build) {
              pushLine();
              build = token.trimStart();
            } else {
              build = candidate;
            }
          }
          pushLine();

          // Estimate baseline offset similar to active path
          const getBaselineTopOffset = (fontSizePx: number): number => {
            // Approximate: many fonts have ~0.8 of font size from top to baseline
            return Math.round(fontSizePx * 0.8);
          };
          const baselineTop = getBaselineTopOffset(fontPx);
          const contentHeight = Math.max(lineHeight, lines.length * lineHeight);

          // Draw with rotation around center like active path
          ctx.save();
          const originX = Math.round((xPx || 0) + maxW / 2);
          const originY = Math.round((yPx || 0) + contentHeight / 2);
          ctx.translate(originX, originY);
          if (tb.rotation) ctx.rotate((tb.rotation * Math.PI) / 180);
          ctx.translate(-maxW / 2, -contentHeight / 2);

          let y = baselineTop;
          for (const text of lines) {
            let drawX = 0;
            const w = ctx.measureText(text).width;
            if (tb.textAlign === "center") drawX = (maxW - w) / 2;
            if (tb.textAlign === "right") drawX = maxW - w;
            ctx.fillText(text, drawX, y);
            // Decorations
            try {
              const decoColor = tb.fill || "#000000";
              const lineThickness = Math.max(1, Math.round(fontPx * 0.06));
              if (tb.isUnderline) {
                const underlineY = Math.round(y + fontPx * 0.08);
                ctx.save();
                ctx.strokeStyle = decoColor;
                ctx.lineWidth = lineThickness;
                ctx.beginPath();
                ctx.moveTo(drawX, underlineY);
                ctx.lineTo(drawX + w, underlineY);
                ctx.stroke();
                ctx.restore();
              }
              if (tb.isStrikethrough) {
                const strikeY = Math.round(y - fontPx * 0.3);
                ctx.save();
                ctx.strokeStyle = decoColor;
                ctx.lineWidth = lineThickness;
                ctx.beginPath();
                ctx.moveTo(drawX, strikeY);
                ctx.lineTo(drawX + w, strikeY);
                ctx.stroke();
                ctx.restore();
              }
            } catch {
              // Ignore overlay image loading errors
            }
            y += lineHeight;
          }
          ctx.restore();
        }
        return encodeCanvasToDataURL(canvas);
      } catch {
        return null;
      }
    },
    [encodeCanvasToDataURL],
  );

  // Helper function to load image with CORS fallback
  const loadImageWithCorsFallback = async (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      // First try with CORS
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => {
        // If CORS fails, try without crossOrigin but warn about potential tainting
        console.warn(
          "CORS image load failed, loading without CORS (canvas may be tainted):",
          src,
        );
        const fallbackImg = new Image();
        fallbackImg.onload = () => resolve(fallbackImg);
        fallbackImg.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        fallbackImg.src = src;
      };
      img.src = src;
    });
  };

  // Convert data URL to temporary object URL for instant preview
  const toObjectUrl = (dataUrl: string): string => {
    try {
      // Validate data URL format
      if (!dataUrl || !dataUrl.startsWith("data:")) {
        console.warn("Invalid data URL:", dataUrl);
        return dataUrl;
      }

      const parts = dataUrl.split(",");
      const meta = parts[0] || "";
      const b64 = parts[1] || "";

      // Check if base64 data exists
      if (!b64) {
        console.warn("Empty base64 data in data URL:", dataUrl);
        return dataUrl;
      }

      const mimeMatch = /data:(.*?);base64/.exec(meta);
      const mime = mimeMatch?.[1] || "image/png";

      // Validate base64 data
      try {
        const bin = atob(b64);
        if (bin.length === 0) {
          console.warn("Decoded base64 data is empty");
          return dataUrl;
        }

        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);

        const blob = new Blob([buf], { type: mime });

        // Validate blob size
        if (blob.size === 0) {
          console.warn("Created blob has zero size");
          return dataUrl;
        }

        return URL.createObjectURL(blob);
      } catch (decodeError) {
        console.warn("Failed to decode base64 data:", decodeError);
        return dataUrl;
      }
    } catch (error) {
      console.warn("Error in toObjectUrl:", error);
      return dataUrl;
    }
  };

  // Use the same scaling and font sizing as Grab Text preview
  const computeGrabDisplayBox = (
    extractedBox: {
      x: number;
      y: number;
      width: number;
      height: number;
      text: string;
      font_characteristics?: { style?: string; weight?: string };
      text_alignment?: string;
      text_color?: number[];
    },
    currentImg: HTMLImageElement | null,
    natural: { width: number; height: number } | null,
  ) => {
    const safe = natural ?? { width: 1, height: 1 };
    const scaleX = currentImg ? currentImg.clientWidth / safe.width : 1;
    const scaleY = currentImg ? currentImg.clientHeight / safe.height : 1;
    const scaledX = extractedBox.x * scaleX;
    const scaledY = extractedBox.y * scaleY;
    const scaledWidth = extractedBox.width * scaleX;
    const scaledHeight = extractedBox.height * scaleY;
    const fontWeight: "normal" | "bold" =
      extractedBox.font_characteristics?.weight === "semibold" ||
      extractedBox.font_characteristics?.weight === "bold"
        ? "bold"
        : "normal";
    const fontStyle = extractedBox.font_characteristics?.style;
    const fontSize = measureOptimalLatoFontSize(
      extractedBox.text,
      scaledWidth,
      scaledHeight,
      fontWeight,
      fontStyle,
    );
    return {
      scaledX,
      scaledY,
      scaledWidth,
      scaledHeight,
      fontSize,
      fontWeight,
      fontStyle,
    };
  };

  // Ensure Lato is available from the start for grab-text preview accuracy
  useEffect(() => {
    try {
      ensureGoogleFontLoaded("Lato");
      requestFontFace("Lato");
    } catch {
      // Ignore font loading errors
    }
  }, [ensureGoogleFontLoaded, requestFontFace]);

  // When opening the text tab or search changes, reset pagination
  useEffect(() => {
    if (activeSidebarTab !== "text") return;
    setVisibleCount(PAGE_SIZE);
  }, [activeSidebarTab, fontSearch]);

  // Grab text functionality
  const grabFlowHandlesRef = useRef<Map<string, GrabTextFlowHandle>>(new Map());

  const handleGrabText = () => {
    const accountId = selectedAccount?.id;
    const currentImage = activeImage;
    if (!accountId || !currentImage) return;

    // If text has already been extracted/applied before, show the 'already extracted' panel
    // Check both server-attached metadata and hydrated client_grabbed_image metadata
    const currentForIndex = polledMediaList?.find((img) => img.id === activeImageId);
    const clientBoxesLen = Number(
      currentForIndex?.client_grabbed_image?.grab_text_metadata?.text_metadata?.text_boxes
        ?.length || 0,
    );
    const serverBoxesLen = Number(
      (currentForIndex?.grab_text_metadata || currentImage?.grab_text_metadata)
        ?.text_metadata?.text_boxes?.length || 0,
    );
    const hasExistingMetadata = clientBoxesLen > 0 || serverBoxesLen > 0;
    if (hasExistingMetadata) {
      const id = currentForIndex?.id ?? currentImage.id;
      setGrabStatesByImageId((prev) => ({
        ...prev,
        [id]: {
          isActive: false,
          loading: false,
          progress: 100,
          success: false,
          tempImage: null,
          tempMetadata: null,
          alreadyGrabbed: true,
        },
      }));
      setActiveSidebarTab("text");
      return;
    }

    // Cancel any previous handle for this image
    grabFlowHandlesRef.current.get(currentImage.id)?.cancel();

    const handle = runGrabTextFlow({
      accountId: String(accountId),
      imageId: currentImage.id,
      existingGrabTextImageId: currentImage.metadata?.grab_text_image_id,
      setGrabStatesByImageId,
      onReplaceActiveImage: (updatedImage: Image) => {
        // Keep original image in place; update polled map for fetched details
        setPolledMediaMap((prev) => ({
          ...prev,
          [currentImage.id]: {
            ...(prev[currentImage.id] || currentImage),
            ...updatedImage,
          } as Image,
        }));
        // Also set tempImage immediately so the image switches during grab text processing
        setGrabStatesByImageId((prev) => {
          const s = prev[currentImage.id];
          if (!s) return prev;
          return { ...prev, [currentImage.id]: { ...s, tempImage: updatedImage } };
        });
      },
      onAttachClientGrabbed: (originalImageId: string, grabbedImage: Image) => {
        // Update the original image with grab_text_metadata when grab text succeeds
        setPolledMediaMap((prev) => ({
          ...prev,
          [originalImageId]: {
            ...(prev[originalImageId] || currentImage),
            grab_text_metadata: grabbedImage?.grab_text_metadata || null,
          } as Image,
        }));

        // Also update the adCreative so grab_text_metadata persists on reload (local state only)
        if (currentAdCreativeRef.current) {
          const updatedImages = currentAdCreativeRef.current.creative.images.map((img) =>
            img.id === originalImageId
              ? {
                  ...img,
                  grab_text_metadata: grabbedImage?.grab_text_metadata || undefined,
                }
              : img,
          );

          currentAdCreativeRef.current = {
            ...currentAdCreativeRef.current,
            creative: {
              ...currentAdCreativeRef.current.creative,
              images: updatedImages,
            },
          };
        }
      },
    });
    grabFlowHandlesRef.current.set(currentImage.id, handle);
  };

  const cancelGrabText = (imageId?: string) => {
    if (!imageId) return;
    // Cancel with common flow
    grabFlowHandlesRef.current.get(imageId)?.cancel();
    // Move to Text tab on cancel (persist per-image)
    handleTabSwitch("text");
  };

  const handleCancelGrabText = () => {
    // Cancel any ongoing grab text operations
    const img = editorMediaList?.find((img) => img.id === activeImageId);
    cancelGrabText(img?.id);

    // Reset all grab text state and revert to original image
    if (img?.id) {
      setGrabStatesByImageId((prev) => ({
        ...prev,
        [img.id]: {
          isActive: false,
          loading: false,
          progress: 0,
          success: false,
          tempImage: null,
          tempMetadata: null,
        },
      }));

      // Clear grab_text_metadata from the image to allow grab text to run again
      setPolledMediaMap((prev) => ({
        ...prev,
        [img.id]: {
          ...(prev[img.id] || img),
          grab_text_metadata: undefined,
        } as Image,
      }));

      // Also clear grab_text_metadata from the adCreative so it doesn't persist across dialog reloads (local state only)
      if (currentAdCreativeRef.current) {
        const updatedImages = currentAdCreativeRef.current.creative.images.map((image) =>
          image.id === img.id
            ? {
                ...image,
                grab_text_metadata: undefined,
              }
            : image,
        );

        currentAdCreativeRef.current = {
          ...currentAdCreativeRef.current,
          creative: {
            ...currentAdCreativeRef.current.creative,
            images: updatedImages,
          },
        };
      }
    }
    // Always move to Text tab on cancel
    handleTabSwitch("text");
  };

  // Exit any grab overlay views and return to normal Text tab editing
  const handleBackToTextFromGrab = () => {
    const img = editorMediaList?.find((img) => img.id === activeImageId);
    if (img?.id) {
      setGrabStatesByImageId((prev) => ({
        ...prev,
        [img.id]: {
          isActive: false,
          loading: false,
          progress: 0,
          success: false,
          tempImage: null,
          tempMetadata: null,
          alreadyGrabbed: false,
        },
      }));
    }
    handleTabSwitch("text");
  };

  const handleConfirmGrabText = () => {
    const img = editorMediaList?.find((img) => img.id === activeImageId);
    const state = img ? grabStatesByImageId[img.id] : undefined;
    // Stop any active polling/progress for this image to avoid racey success flips
    if (img?.id) {
      grabFlowHandlesRef.current.get(img.id)?.cancel();
    }

    // Keep original image; attach grabbed image alongside for reference
    if (state?.tempImage && img?.id) {
      setPolledMediaMap((prev) => {
        const base = (prev[img.id] || img) as Image & { client_grabbed_image?: Image };
        return {
          ...prev,
          [img.id]: { ...base, client_grabbed_image: state.tempImage } as Image,
        };
      });
    }

    // Convert extracted text boxes to editable text boxes
    if (state?.tempMetadata?.text_metadata?.text_boxes && imgSize) {
      // const currentImg = getCurrentImgRef(); // Removed unused variable
      // Calculate scale factors from original image to displayed image
      // const scaleX = currentImg ? currentImg.clientWidth / imgSize.width : 1; // Removed unused variable
      // const scaleY = currentImg ? currentImg.clientHeight / imgSize.height : 1; // Removed unused variable

      const convertedTextBoxes: TextBox[] =
        state.tempMetadata.text_metadata.text_boxes.map((extractedBox, index) => {
          // Convert font weight to boolean flags
          const isBold =
            extractedBox.font_characteristics.weight === "semibold" ||
            extractedBox.font_characteristics.weight === "bold";
          const isItalic = extractedBox.font_characteristics.style === "italic";

          // Convert RGB array to hex color
          const hexColor = `#${extractedBox.text_color
            .map((c) => Math.round(c).toString(16).padStart(2, "0"))
            .join("")}`;

          // Calculate display scaled box and font using the same logic as preview
          const currentImg = getCurrentImgRef();
          const grab = computeGrabDisplayBox(extractedBox, currentImg, imgSize);

          return {
            id: `grabbed-text-${Date.now()}-${index}`,
            x: grab.scaledX,
            y: grab.scaledY,
            text: extractedBox.text,
            fontSize: grab.fontSize,
            fontFamily: "Lato",
            fill: hexColor,
            width: grab.scaledWidth,
            rotation: 0,
            isBold: isBold,
            isItalic: isItalic,
            isUnderline: false,
            isStrikethrough: false,
            textAlign: extractedBox.text_alignment,
            isSelected: true, // Start in selected state
            isEditing: false,
          };
        });

      // Get existing text boxes for the current image
      const currentImageId = String(baseActiveImage?.id || "");
      const existingTextBoxes = editConfigs[currentImageId] || [];

      // Add the converted text boxes to the existing ones
      const updatedTextBoxes = [...existingTextBoxes, ...convertedTextBoxes];

      // Update the text boxes for the current image
      handleTextBoxesChange(updatedTextBoxes);
    } else if (!imgSize) {
      // No image size available, skip processing
    } else {
      // No text boxes to process
    }

    // Clear per-image grab state so sidebar returns to idle (no 'already extracted')
    if (img?.id) {
      setGrabStatesByImageId((prev) => {
        const next = { ...prev } as Record<string, (typeof prev)[string]>;
        delete next[img.id];
        return next;
      });
    }
    handleTabSwitch("text");
  };

  const handleTabSwitch = (newTab: SidebarTab) => {
    // If switching away from grab-text and we have grab text loading or success state, cancel it
    // No special handling for grab-text tab anymore since it lives inside Text tab

    // When entering grab-text, stop regular polling to prevent races
    // No-op for grab-text

    // Normal tab switching + persist per-image
    setActiveSidebarTab(newTab);
    const imgId = baseActiveImage?.id;
    if (imgId) {
      setSidebarTabByImageId((prev) => ({ ...prev, [imgId]: newTab }));
    }

    // If switching away from text tab, clear magic edit state
    if (newTab !== "text") {
      setIsMagicEditActive(false);
      // Note: We don't clear magicEditStateByImageId as it should persist across tab switches
    }
  };

  // Helper function to handle magic edit state
  const handleMagicEditToggle = (active: boolean) => {
    const imgId = baseActiveImage?.id;
    if (!imgId) return;

    setIsMagicEditActive(active);

    // Initialize magic edit state for this image if not exists
    if (active && !magicEditStateByImageId[imgId]) {
      setMagicEditStateByImageId((prev) => ({
        ...prev,
        [imgId]: {
          prompt: "",
          selectedReferenceImageId: null,
          pollingImageId: null,
          isProcessing: false,
          baseImageId: imgId, // Track which base image this magic edit belongs to
          history: [], // Initialize empty history
          isLoadingHistory: false, // Initialize loading state
          isLoadingMore: false, // Initialize loading more state
          nextCursor: null, // Initialize pagination cursor
          hasMore: false, // Initialize pagination flag
        },
      }));
    }

    // Ensure we're on the text tab when activating magic edit
    if (active && activeSidebarTab !== "text") {
      handleTabSwitch("text");
    }
  };
  // Preview sizing
  const previewRef = useRef<HTMLDivElement>(null);
  const imageRowRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const [isImageReady, setIsImageReady] = useState(false);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const previousActiveIndexRef = useRef<string | null>(null);
  const switchStartRef = useRef<number>(0);
  const [deselectToken, setDeselectToken] = useState(0);
  // Track when text boxes are actively interacting (dragging/resizing)
  const [isInteracting, setIsInteracting] = useState(false);

  // Cache per-image display↔natural scales to ensure correct save for non-active images
  const scaleCacheRef = useRef<
    Map<
      string,
      {
        scaleX: number;
        scaleY: number;
        displayedW: number;
        displayedH: number;
        naturalW: number;
        naturalH: number;
      }
    >
  >(new Map());
  // Add-image aspect ratio hover menu state
  const [showAspectMenu, setShowAspectMenu] = useState(false);
  const [aspectMenuPos, setAspectMenuPos] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const hideAspectTimerRef = useRef<number | null>(null);
  // Handle text box changes
  // Per-image text box configurations
  const [editConfigs, setEditConfigs] = useState<Record<string, TextBox[]>>({});

  // =========================
  // Undo/Redo History Support
  // =========================
  type EditorHistorySnapshot = {
    text: Record<string, TextBox[]>;
    images: Record<string, OverlayImageBox[]>;
    visibleIds: string[];
    activeId: string | null;
    clientGrabbed: Record<
      string,
      {
        url?: string;
        permalink_url?: string;
        metadata?: GrabTextMetadata | null;
      } | null
    >;
  };
  const history = useUndoRedo<EditorHistorySnapshot>(
    {
      text: {},
      images: {},
      visibleIds: (baseMediaList || []).map((m) => String(m.id)),
      activeId: baseMediaList?.find((img) => img.id === activeImageId)?.id ?? null,
      clientGrabbed: {},
    },
    { capacity: 50 },
  );
  const isApplyingHistoryRef = useRef<boolean>(false);
  const suppressNextCommitRef = useRef<boolean>(false);
  const suppressCommitUntilRef = useRef<number>(0);
  const initializedHistoryCreativeIdRef = useRef<string | number | null>(null);
  const currentSnapshot = useMemo<EditorHistorySnapshot>(
    () => ({
      text: editConfigs,
      images: imageEditConfigs,
      visibleIds: visibleImageIds,
      activeId: editorMediaList?.find((img) => img.id === activeImageId)?.id ?? null,
      clientGrabbed: (() => {
        type ClientGrabShape = {
          permalink_url?: string;
          url?: string;
          grab_text_metadata?: GrabTextMetadata | null;
        } | null;
        const map: Record<string, ClientGrabShape> = {};
        try {
          for (const im of editorMediaList || []) {
            const id = String(im.id);
            // Access polledMediaMap directly instead of including it in dependencies
            // This prevents unnecessary snapshot creation when polling updates occur
            const polled = polledMediaMap?.[id] || im;
            const cg = (
              polled as unknown as {
                client_grabbed_image?: ClientGrabShape;
              }
            )?.client_grabbed_image;
            if (cg && (cg.permalink_url || cg.url)) {
              map[id] = {
                url: cg.url,
                permalink_url: cg.permalink_url,
                grab_text_metadata: cg.grab_text_metadata ?? null,
              } as unknown as ClientGrabShape;
            } else {
              map[id] = null;
            }
          }
        } catch {
          // ignore errors during snapshot
        }
        return map;
      })(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editConfigs,
      imageEditConfigs,
      visibleImageIds,
      editorMediaList,
      activeImageId,
      // Removed polledMediaMap from dependencies to prevent duplicate snapshots
      // during image polling when content hasn't actually changed
    ],
  );

  // Deep comparison function to check if snapshots have actually changed
  const snapshotsEqual = useCallback(
    (a: EditorHistorySnapshot, b: EditorHistorySnapshot): boolean => {
      // Compare basic properties
      if (a.activeId !== b.activeId) return false;
      if (a.visibleIds.length !== b.visibleIds.length) return false;

      // Compare visibleIds arrays
      const sortedA = [...a.visibleIds].sort();
      const sortedB = [...b.visibleIds].sort();
      for (let i = 0; i < sortedA.length; i++) {
        if (sortedA[i] !== sortedB[i]) return false;
      }

      // Compare text boxes for each image
      const allTextKeys = new Set([...Object.keys(a.text), ...Object.keys(b.text)]);
      for (const key of allTextKeys) {
        const textA = a.text[key] || [];
        const textB = b.text[key] || [];
        if (textA.length !== textB.length) return false;

        for (let i = 0; i < textA.length; i++) {
          const boxA = textA[i];
          const boxB = textB[i];
          if (
            boxA.id !== boxB.id ||
            boxA.text !== boxB.text ||
            boxA.x !== boxB.x ||
            boxA.y !== boxB.y ||
            boxA.width !== boxB.width ||
            boxA.fontSize !== boxB.fontSize ||
            boxA.rotation !== boxB.rotation ||
            boxA.fontFamily !== boxB.fontFamily ||
            boxA.fill !== boxB.fill ||
            boxA.isBold !== boxB.isBold ||
            boxA.isItalic !== boxB.isItalic ||
            boxA.isUnderline !== boxB.isUnderline ||
            boxA.isStrikethrough !== boxB.isStrikethrough ||
            boxA.textAlign !== boxB.textAlign ||
            boxA.isSelected !== boxB.isSelected ||
            boxA.isEditing !== boxB.isEditing
          ) {
            return false;
          }
        }
      }

      // Compare image overlays for each image
      const allImageKeys = new Set([...Object.keys(a.images), ...Object.keys(b.images)]);
      for (const key of allImageKeys) {
        const imagesA = a.images[key] || [];
        const imagesB = b.images[key] || [];
        if (imagesA.length !== imagesB.length) return false;

        for (let i = 0; i < imagesA.length; i++) {
          const imgA = imagesA[i];
          const imgB = imagesB[i];
          if (
            imgA.id !== imgB.id ||
            imgA.src !== imgB.src ||
            imgA.x !== imgB.x ||
            imgA.y !== imgB.y ||
            imgA.width !== imgB.width ||
            imgA.height !== imgB.height ||
            imgA.rotation !== imgB.rotation
          ) {
            return false;
          }
        }
      }

      // Compare client grabbed data
      const allClientKeys = new Set([
        ...Object.keys(a.clientGrabbed),
        ...Object.keys(b.clientGrabbed),
      ]);
      for (const key of allClientKeys) {
        const clientA = a.clientGrabbed[key];
        const clientB = b.clientGrabbed[key];

        // Both null/undefined
        if (!clientA && !clientB) continue;

        // One null, one not
        if (!clientA || !clientB) return false;

        // Compare properties
        if (
          clientA.url !== clientB.url ||
          clientA.permalink_url !== clientB.permalink_url ||
          JSON.stringify(clientA.metadata) !== JSON.stringify(clientB.metadata)
        ) {
          return false;
        }
      }

      return true;
    },
    [],
  );

  const applySnapshot = useCallback((snap: EditorHistorySnapshot) => {
    try {
      isApplyingHistoryRef.current = true;
      suppressNextCommitRef.current = true;
      suppressCommitUntilRef.current = Date.now() + 700; // allow state to settle
      setEditConfigs(snap.text || {});
      setImageEditConfigs(snap.images || {});
      if (Array.isArray(snap.visibleIds)) setVisibleImageIds(snap.visibleIds);
      // Determine index by active id after visibility applied
      const id = snap.activeId || null;
      if (id) {
        setActiveImageId(id);
      }
      // Cancel polling for any ids no longer present in the target snapshot; mark them to hide on dialog close;
      // re-enable for those that reappear (redo)
      try {
        const targetIds = new Set((snap.visibleIds || []).map((v) => String(v)));
        // Cancel all controllers/timeouts for ids NOT in target
        for (const [id, controller] of Array.from(pollControllersRef.current.entries())) {
          if (!targetIds.has(id)) {
            suppressedPollingIdsRef.current.add(id);
            // Defer hide/delete until dialog closes; explicit removed => delete
            try {
              controller.abort();
            } catch {}
            pollControllersRef.current.delete(id);
          }
        }
        for (const [id, t] of Array.from(pollTimeoutsRef.current.entries())) {
          if (!targetIds.has(id)) {
            if (t) clearTimeout(t as unknown as number);
            pollTimeoutsRef.current.delete(id);
          }
        }
        for (const id of Array.from(scheduledPollIdsRef.current.values())) {
          if (!targetIds.has(id)) scheduledPollIdsRef.current.delete(id);
        }
        // Allow polling for any ids present in target (redo case) and clear pending hide/delete/explicit flags
        for (const id of targetIds) {
          suppressedPollingIdsRef.current.delete(id);
        }
      } catch {}
      // Reconcile client_grabbed_image state to restore or clear grabbed preview
      if (snap.clientGrabbed) {
        const entries = Object.entries(snap.clientGrabbed);
        if (entries.length) {
          setPolledMediaMap((prev) => {
            const next = { ...prev } as Record<string, Image>;
            for (const [imgId, payload] of entries) {
              const base = next[imgId] || ({} as Image);
              const merged = { ...base } as Image & {
                client_grabbed_image?: {
                  id: string;
                  url?: string;
                  permalink_url?: string;
                  hash?: string;
                  width?: number;
                  height?: number;
                  grab_text_metadata?: GrabTextMetadata | null;
                };
              };
              if (payload && (payload.permalink_url || payload.url)) {
                const cg =
                  merged.client_grabbed_image ||
                  ({ id: "", url: "", hash: "", width: 0, height: 0 } as const);
                merged.client_grabbed_image = {
                  ...cg,
                  url: payload.url ?? cg.url,
                  permalink_url: payload.permalink_url ?? (cg as Image).permalink_url,
                  grab_text_metadata: payload.metadata ?? undefined,
                };
              } else if (merged.client_grabbed_image) {
                delete merged.client_grabbed_image;
              }
              next[imgId] = merged as Image;
            }
            return next;
          });
          // Clear any temporary grab overlay state so the original shows
          setGrabStatesByImageId((prev) => {
            const next = { ...prev } as typeof prev;
            for (const imgId of Object.keys(snap.clientGrabbed)) delete next[imgId];
            return next;
          });
        }
      }
    } finally {
      // Defer flag reset to the next macrotask so dependent effects can skip one cycle
      setTimeout(() => {
        isApplyingHistoryRef.current = false;
      }, 0);
    }
  }, []);
  // Initialize history when a creative loads its config for the first time in this session
  useEffect(() => {
    const cid = adCreative?.id ?? null;
    if (cid == null) return;
    if (initializedHistoryCreativeIdRef.current === cid) return;
    // Seed once we have any existing boxes (or even empty baseline)
    const hasData =
      Object.keys(currentSnapshot.text).length > 0 ||
      Object.keys(currentSnapshot.images).length > 0;
    if (!hasData) return;
    history.reset(currentSnapshot);
    initializedHistoryCreativeIdRef.current = cid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreative?.id, currentSnapshot]);
  // Debounced commit of snapshots to avoid spamming history during rapid edits/typing
  const commitTimerRef = useRef<number | null>(null);
  const hasTempVisible = useCallback(() => {
    try {
      return (visibleImageIds || []).some((id) => String(id).startsWith("temp-"));
    } catch {
      return false;
    }
  }, [visibleImageIds]);
  const scheduleCommit = useCallback(() => {
    if (commitTimerRef.current !== null) window.clearTimeout(commitTimerRef.current);
    commitTimerRef.current = window.setTimeout(() => {
      if (isApplyingHistoryRef.current) return;
      if (suppressNextCommitRef.current) {
        suppressNextCommitRef.current = false;
        return;
      }
      if (Date.now() < suppressCommitUntilRef.current) return;
      if (isInteracting) return;
      // Do not commit temp/placeholder states to history
      if (hasTempVisible()) return;
      // Use deep comparison to ensure snapshots are only committed when content actually changes
      if (!snapshotsEqual(history.value, currentSnapshot)) {
        history.set(currentSnapshot);
      }
    }, 400);
  }, [currentSnapshot, history, isInteracting, hasTempVisible, snapshotsEqual]);
  useEffect(() => {
    scheduleCommit();
    return () => {
      if (commitTimerRef.current !== null) window.clearTimeout(commitTimerRef.current);
    };
  }, [currentSnapshot, scheduleCommit]);
  // Keyboard shortcuts: Cmd/Ctrl+Z (undo), Shift+Cmd/Ctrl+Z (redo)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const isMod = isMac ? e.metaKey : e.ctrlKey;
      if (!isMod) return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        const snap = history.undo();
        if (snap) applySnapshot(snap);
      } else if (e.key.toLowerCase() === "z" && e.shiftKey) {
        e.preventDefault();
        const snap = history.redo();
        if (snap) applySnapshot(snap);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [applySnapshot, history]);

  // Helpers: parse aspect ratio like "9:16" → { rw: 9, rh: 16 }
  const parseAspectRatio = useCallback(
    (ratio?: string | null): { rw: number; rh: number } | null => {
      if (!ratio) return null;
      const parts = ratio.split(":");
      if (parts.length !== 2) return null;
      const rw = Number(parts[0]);
      const rh = Number(parts[1]);
      if (!Number.isFinite(rw) || !Number.isFinite(rh) || rw <= 0 || rh <= 0) return null;
      return { rw, rh };
    },
    [],
  );

  // Compute contained box size for a given ratio within maxW x maxH
  const computeContainedSizeForRatio = useCallback(
    (
      ratio: string | undefined,
      maxW: number,
      maxH: number,
    ): { width: number; height: number } | null => {
      const parsed = parseAspectRatio(ratio);
      if (!parsed) return null;
      const { rw, rh } = parsed;
      const scale = Math.min(maxW / rw, maxH / rh);
      return { width: Math.round(rw * scale), height: Math.round(rh * scale) };
    },
    [parseAspectRatio],
  );

  // Unified helper: compute scale from display space to natural image space for both active and non-active images
  const getDisplayToNaturalScale = useCallback(
    (params: {
      image:
        | { width?: number; height?: number; aspect_ratio?: string | undefined }
        | null
        | undefined;
      isActive: boolean;
      currentImg: HTMLImageElement | null;
      containerEl: HTMLElement | null;
    }): {
      scaleX: number;
      scaleY: number;
      displayedW: number;
      displayedH: number;
      naturalW: number;
      naturalH: number;
    } => {
      let naturalW = Number(params?.image?.width || 0);
      let naturalH = Number(params?.image?.height || 0);

      // Fallback to DOM intrinsic size for the active image if missing
      if ((!naturalW || !naturalH) && params.isActive && params.currentImg) {
        const nw = Number(params.currentImg.naturalWidth || 0);
        const nh = Number(params.currentImg.naturalHeight || 0);
        if (nw > 0 && nh > 0) {
          naturalW = nw;
          naturalH = nh;
        }
      }

      // Final guard
      if (!naturalW) naturalW = 1;
      if (!naturalH) naturalH = 1;

      let displayedW = 0;
      let displayedH = 0;

      // Prefer precise DOM size for active image
      const dw = params?.currentImg?.clientWidth || 0;
      const dh = params?.currentImg?.clientHeight || 0;
      if (params.isActive && dw > 0 && dh > 0) {
        displayedW = dw;
        displayedH = dh;
      }

      // Fallback to aspect-fit math using container
      if (!displayedW || !displayedH) {
        const maxW = Math.max(0, params?.containerEl?.clientWidth || 0) - 8;
        const maxH = Math.max(0, params?.containerEl?.clientHeight || 0) - 8;
        const box = computeContainedSizeForRatio(
          params?.image?.aspect_ratio,
          maxW,
          maxH,
        ) || { width: naturalW, height: naturalH };
        displayedW = Math.max(1, box.width);
        displayedH = Math.max(1, box.height);
      }

      const scaleX = naturalW / displayedW;
      const scaleY = naturalH / displayedH;

      return { scaleX, scaleY, displayedW, displayedH, naturalW, naturalH };
    },
    [computeContainedSizeForRatio],
  );

  // Build payload for saving edit configuration (v2 - normalized)
  const buildEditorStateForSave = useCallback((): Array<{
    id: string;
    background_image_edit_id?: string;
    edit_configuration?: ImageEditStateV2;
    base64_data?: string | null;
  }> | null => {
    const accountId = String(selectedAccount?.id || "");
    const creativeId = String(adCreative?.id || "");
    if (!accountId || !creativeId) return null;

    const elem = imageRowRef.current;
    if (!elem) return null;

    // Include all visible images in the save payload, even if they are pending
    const visibleImages = (polledMediaList || []).filter((im) =>
      visibleImageIds.includes(im.id),
    );

    return visibleImages.map((im) => {
      // For pending images, only send the id
      if (im.status === "pending") {
        return {
          id: im.id,
        };
      }

      // Find the original index in baseMediaList for accessing editConfigs/imageEditConfigs
      const originalIdx = (baseMediaList || []).findIndex(
        (baseIm) => baseIm.id === im.id,
      );
      const imageId = String(im.id);
      const key = String(im.id || originalIdx);
      // Prefer cached scale (recorded when image was active); fallback to computed
      const cached = scaleCacheRef.current.get(key);
      const { scaleX, scaleY, naturalW, naturalH } =
        cached ||
        getDisplayToNaturalScale({
          image: {
            width: im.width,
            height: im.height,
            aspect_ratio: im.aspect_ratio,
          },
          isActive:
            originalIdx === visibleImages.findIndex((img) => img.id === activeImageId),
          currentImg:
            originalIdx === visibleImages.findIndex((img) => img.id === activeImageId)
              ? getCurrentImgRef()
              : null,
          containerEl: imageRowRef.current,
        });

      const q = (v: number) => Math.round((v || 0) * 1000) / 1000; // quantize to 3 decimals
      const persisted: TextBoxPersistedV2[] = (editConfigs[imageId] || []).map((tb) => {
        const x_px = (tb.x || 0) * scaleX;
        const y_px = (tb.y || 0) * scaleY;
        const w_px = (tb.width || 0) * scaleX;
        const fs_px = (tb.fontSize || 0) * scaleY;
        const x_norm = q(naturalW ? x_px / naturalW : 0);
        const y_norm = q(naturalH ? y_px / naturalH : 0);
        const width_norm = q(naturalW ? w_px / naturalW : 0);
        const font_size_norm = q(naturalH ? fs_px / naturalH : 0);
        return {
          id: tb.id,
          text: tb.text,
          x_norm,
          y_norm,
          width_norm,
          font_size_norm,
          rotation: tb.rotation || 0,
          fontFamily: String(tb.fontFamily || "").trim(),
          fill: String(tb.fill || "").toLowerCase(),
          isBold: !!tb.isBold,
          isItalic: !!tb.isItalic,
          isUnderline: !!tb.isUnderline,
          isStrikethrough: !!tb.isStrikethrough,
          textAlign: String(tb.textAlign || "left") as "left" | "center" | "right",
        };
      });

      const persistedImages = (imageEditConfigs[imageId] || []).map((ib) => {
        const x_px = (ib.x || 0) * scaleX;
        const y_px = (ib.y || 0) * scaleY;
        const w_px = (ib.width || 0) * scaleX;
        const h_px = (ib.height || 0) * scaleY;
        const x_norm = q(naturalW ? x_px / naturalW : 0);
        const y_norm = q(naturalH ? y_px / naturalH : 0);
        const width_norm = q(naturalW ? w_px / naturalW : 0);
        const height_norm = q(naturalH ? h_px / naturalH : 0);
        return {
          src: ib.src,
          x_norm,
          y_norm,
          width_norm,
          height_norm,
          rotation: ib.rotation || 0,
        };
      });

      // Prefer client grabbed image url if present so restore shows same variant
      const current = polledMediaList?.[originalIdx] as Image | undefined;
      const clientGrabMetadata = current?.client_grabbed_image?.grab_text_metadata;

      // Get the background image edit ID from the tracked background image edit object
      const trackedBackgroundImageEdit = currentBackgroundImageEditByImageId[im.id];

      const editConfiguration: ImageEditStateV2 = {
        aspect_ratio: im.aspect_ratio,
        width: naturalW || undefined,
        height: naturalH || undefined,
        original_url: im.raw_data?.permalink_url || im.url,
        grab_text: {
          performed: Boolean(im.metadata?.grab_text_image_id || im.grab_text_metadata),
          applied: (editConfigs[imageId] || []).length > 0,
          grab_text_image_id: im.metadata?.grab_text_image_id ?? null,
          metadata: clientGrabMetadata ?? im.grab_text_metadata ?? null,
        },
        text_boxes: persisted,
        image_overlays: persistedImages,
      };

      return {
        id: im.id,
        background_image_edit_id: trackedBackgroundImageEdit?.id,
        edit_configuration: editConfiguration,
        base64_data: null, // Will be set by the caller if needed
      };
    });
  }, [
    selectedAccount?.id,
    adCreative?.id,
    baseMediaList,
    imageEditConfigs,
    activeImageId,
    editConfigs,
    polledMediaList,
    getCurrentImgRef,
    getDisplayToNaturalScale,
    currentBackgroundImageEditByImageId,
    visibleImageIds,
  ]);

  const cancelHideAspectMenu = () => {
    if (hideAspectTimerRef.current !== null) {
      window.clearTimeout(hideAspectTimerRef.current);
      hideAspectTimerRef.current = null;
    }
  };
  const scheduleHideAspectMenu = () => {
    cancelHideAspectMenu();
    hideAspectTimerRef.current = window.setTimeout(() => {
      setShowAspectMenu(false);
    }, 220);
  };

  const handleDownloadComposite = async () => {
    try {
      // Reuse the active composer to ensure download parity
      const dataUrl = await composeActiveToDataURL();
      if (!dataUrl) return;

      // Convert data URL to blob for download
      const [header, base64Data] = dataUrl.split(",");
      const contentType = header.match(/:(.*?);/)?.[1] || "image/png";
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      // Generate filename with aspect ratio and timestamp
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "-")
        .substring(0, 15); // YYYYMMDD-HHmmss format

      const ratio = activeImage?.aspect_ratio
        ? activeImage.aspect_ratio.replace(":", "x")
        : "unknown";
      const ext = contentType.split("/")[1]?.replace("jpeg", "jpg") || "png";
      const safeName = `getcrux_${ratio}_${timestamp}.${ext}`;

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = safeName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Download failed", e);
    }
  };

  // Compose the current active image+text into a PNG data URL (same pipeline as download)
  const composeActiveToDataURL = useCallback(
    async (opts?: { fast?: boolean }): Promise<string | null> => {
      try {
        const imgElement = getCurrentImgRef();
        const container = imageRowRef.current;
        if (!imgElement || !container || !activeImage) return null;

        const canvas = document.createElement("canvas");
        const finalCtx = canvas.getContext("2d");
        if (!finalCtx) return null;
        const workCanvas = document.createElement("canvas");
        const ctx = workCanvas.getContext("2d");
        if (!ctx) return null;

        const naturalW = imgElement.naturalWidth || activeImage.width || 0;
        const naturalH = imgElement.naturalHeight || activeImage.height || 0;
        if (!naturalW || !naturalH) return null;

        canvas.width = naturalW;
        canvas.height = naturalH;
        finalCtx.imageSmoothingEnabled = true;
        try {
          (
            finalCtx as CanvasRenderingContext2D & { imageSmoothingQuality?: string }
          ).imageSmoothingQuality = "high";
        } catch {
          // Ignore if imageSmoothingQuality is not supported
        }

        const deviceScale =
          typeof window !== "undefined" && window.devicePixelRatio
            ? window.devicePixelRatio
            : 1;
        const exportScale = opts?.fast ? 1 : Math.max(2, deviceScale);
        workCanvas.width = Math.floor(naturalW * exportScale);
        workCanvas.height = Math.floor(naturalH * exportScale);
        ctx.scale(exportScale, exportScale);
        ctx.imageSmoothingEnabled = true;
        try {
          (
            ctx as CanvasRenderingContext2D & { imageSmoothingQuality?: string }
          ).imageSmoothingQuality = "high";
        } catch {
          // Ignore if imageSmoothingQuality is not supported
        }

        // Reuse the current <img> element when possible to avoid re-decoding in fast mode
        if (opts?.fast && imgElement.complete && (imgElement.naturalWidth || 0) > 0) {
          ctx.drawImage(imgElement, 0, 0, naturalW, naturalH);
        } else {
          const base = await loadImageWithCorsFallback(imageUrl ?? "");
          ctx.drawImage(base, 0, 0, naturalW, naturalH);
        }

        const currentImageId = String(baseActiveImage?.id || "");
        const boxes = editConfigs[currentImageId] || [];
        ctx.textBaseline = "alphabetic";
        const displayedW = imgElement.offsetWidth || naturalW;
        const displayedH = imgElement.offsetHeight || naturalH;
        const scaleX = naturalW / Math.max(1, displayedW);
        const scaleY = naturalH / Math.max(1, displayedH);
        const dpr =
          typeof window !== "undefined" && window.devicePixelRatio
            ? window.devicePixelRatio
            : 1;
        const snap = (v: number) => Math.round(v * dpr) / dpr;
        const imgOffsetLeft = imgElement.offsetLeft || 0;
        const imgOffsetTop = imgElement.offsetTop || 0;

        // Draw image overlays first (under text)
        try {
          const currentImageId = String(baseActiveImage?.id || "");
          const overlays = imageEditConfigs[currentImageId] || [];
          for (const ov of overlays) {
            try {
              const overlayImg = await loadImageWithCorsFallback(ov.src || "");
              const x = (ov.x || 0) * scaleX;
              const y = (ov.y || 0) * scaleY;
              const w = Math.max(1, (ov.width || 0) * scaleX);
              const h = Math.max(1, (ov.height || 0) * scaleY);
              const iw = overlayImg.naturalWidth || 1;
              const ih = overlayImg.naturalHeight || 1;
              // object-fit: contain
              const scaleContain = Math.min(w / iw, h / ih);
              const rw = iw * scaleContain;
              const rh = ih * scaleContain;
              const dx = (w - rw) / 2;
              const dy = (h - rh) / 2;
              ctx.save();
              const originX = Math.round(x + w / 2);
              const originY = Math.round(y + h / 2);
              ctx.translate(originX, originY);
              if (ov.rotation) ctx.rotate(((ov.rotation || 0) * Math.PI) / 180);
              ctx.translate(-w / 2, -h / 2);
              // clip to box area to mimic CSS overflow hidden
              ctx.beginPath();
              ctx.rect(0, 0, w, h);
              ctx.clip();
              ctx.drawImage(overlayImg, dx, dy, rw, rh);
              ctx.restore();
            } catch {
              // Ignore overlay image loading errors
            }
          }
        } catch {
          // Ignore overlay processing errors
        }

        const baselineCache = new Map<string, number>();
        // const measureHeightCache = new Map<string, number>(); // Removed unused variable
        const getBaselineTopOffset = (
          fontSizePx: number,
          fontFamilyName: string,
          fontWeightStr: string,
          fontStyleStr: string,
        ): number => {
          const key = `${fontStyleStr}|${fontWeightStr}|${Math.round(fontSizePx)}|${fontFamilyName}`;
          const cached = baselineCache.get(key);
          if (typeof cached === "number") return cached;
          const container = document.createElement("div");
          container.style.position = "absolute";
          container.style.visibility = "hidden";
          container.style.left = "-99999px";
          container.style.top = "-99999px";
          container.style.margin = "0";
          container.style.padding = "0";
          container.style.whiteSpace = "nowrap";
          container.style.lineHeight = "1.2";
          container.style.fontSize = `${fontSizePx}px`;
          container.style.fontFamily = fontFamilyName;
          container.style.fontWeight = fontWeightStr;
          container.style.fontStyle = fontStyleStr;
          const text = document.createElement("span");
          text.textContent = "Mg";
          const baseline = document.createElement("span");
          baseline.style.display = "inline-block";
          baseline.style.width = "0";
          baseline.style.height = "0";
          baseline.style.verticalAlign = "baseline";
          container.appendChild(text);
          container.appendChild(baseline);
          document.body.appendChild(container);
          const cRect = container.getBoundingClientRect();
          const bRect = baseline.getBoundingClientRect();
          const offset = bRect.top - cRect.top;
          document.body.removeChild(container);
          baselineCache.set(key, offset);
          return offset;
        };

        const loadedFontKeys = new Set<string>();
        for (const tb of boxes) {
          const displayedX = (tb.x || 0) - imgOffsetLeft;
          const displayedY = (tb.y || 0) - imgOffsetTop;
          const tbX = snap(displayedX * scaleX);
          const tbY = snap(displayedY * scaleY);
          const tbWidth = snap(Math.max(10, (tb.width || 200) * scaleX));
          const displayFontPx = tb.fontSize || 14;
          const fontPx = displayFontPx * scaleY;
          const lineHeight = fontPx * 1.2;
          const fontWeight = tb.isBold ? "700" : "400";
          const fontStyle = tb.isItalic ? "italic" : "normal";
          ctx.font = `${fontStyle} ${fontWeight} ${fontPx}px ${tb.fontFamily || "sans-serif"}`;
          ctx.fillStyle = tb.fill || "#000000";
          try {
            const fontKey = `${fontStyle} ${fontWeight} ${Math.round(fontPx)}px ${tb.fontFamily || "sans-serif"}`;
            if (!loadedFontKeys.has(fontKey) && document.fonts?.load) {
              await document.fonts.load(
                `${Math.round(fontPx)}px ${tb.fontFamily || "sans-serif"}`,
              );
              loadedFontKeys.add(fontKey);
            }
          } catch {
            // Ignore font loading errors
          }
          const src = tb.text || "";
          const tokens = src.split(/(\s+)/);
          let line = "";
          const lines: string[] = [];
          const pushLine = () => {
            if (line.length) {
              lines.push(line.replace(/\s+$/, ""));
              line = "";
            }
          };
          for (const token of tokens) {
            if (!/\s+/.test(token)) {
              const test = line + token;
              if (ctx.measureText(test).width <= tbWidth || line === "") {
                if (ctx.measureText(test).width <= tbWidth) {
                  line = test;
                  continue;
                }
              }
              if (line) pushLine();
              let chunk = "";
              for (const ch of token) {
                const probe = chunk + ch;
                if (ctx.measureText(probe).width > tbWidth && chunk) {
                  lines.push(chunk);
                  chunk = ch;
                } else {
                  chunk = probe;
                }
              }
              if (chunk) {
                line = chunk + " ";
                continue;
              }
            }
            const candidate = line + token;
            if (ctx.measureText(candidate).width > tbWidth && line) {
              pushLine();
              line = token.trimStart();
            } else {
              line = candidate;
            }
          }
          pushLine();
          // Avoid DOM height measurement: approximate as numberOfLines * lineHeight in display space
          const displayContentHeight = Math.max(
            displayFontPx * 1.2,
            Math.ceil(displayFontPx * 1.2 * Math.max(1, lines.length)),
          );
          const contentHeight = snap(displayContentHeight * scaleY);
          ctx.save();
          const originX = snap(tbX + tbWidth / 2);
          const originY = snap(tbY + contentHeight / 2);
          ctx.translate(originX, originY);
          if (tb.rotation) ctx.rotate((tb.rotation * Math.PI) / 180);
          ctx.translate(-tbWidth / 2, -contentHeight / 2);
          const baselineTopDisplay = getBaselineTopOffset(
            displayFontPx,
            tb.fontFamily || "sans-serif",
            fontWeight,
            fontStyle,
          );
          let y = snap(baselineTopDisplay * scaleY);
          for (const l of lines) {
            let drawX = 0;
            const w = ctx.measureText(l).width;
            if (tb.textAlign === "center") drawX = (tbWidth - w) / 2;
            if (tb.textAlign === "right") drawX = tbWidth - w;
            ctx.fillText(l, drawX, y);
            // Decorations
            try {
              const decoColor = tb.fill || "#000000";
              const lineThickness = Math.max(1, Math.round(fontPx * 0.06));
              if (tb.isUnderline) {
                const underlineY = Math.round(y + fontPx * 0.08);
                ctx.save();
                ctx.strokeStyle = decoColor;
                ctx.lineWidth = lineThickness;
                ctx.beginPath();
                ctx.moveTo(drawX, underlineY);
                ctx.lineTo(drawX + w, underlineY);
                ctx.stroke();
                ctx.restore();
              }
              if (tb.isStrikethrough) {
                const strikeY = Math.round(y - fontPx * 0.3);
                ctx.save();
                ctx.strokeStyle = decoColor;
                ctx.lineWidth = lineThickness;
                ctx.beginPath();
                ctx.moveTo(drawX, strikeY);
                ctx.lineTo(drawX + w, strikeY);
                ctx.stroke();
                ctx.restore();
              }
            } catch {
              // Ignore overlay image loading errors
            }
            y += lineHeight;
          }
          ctx.restore();
        }
        finalCtx.drawImage(workCanvas, 0, 0, naturalW, naturalH);
        if (opts?.fast) {
          // Skip heavy encoding in fast mode; return null to avoid unnecessary payloads
          return null;
        }
        return encodeCanvasToDataURL(canvas);
      } catch {
        return null;
      }
    },
    [
      getCurrentImgRef,
      activeImage,
      imageEditConfigs,
      baseActiveImage?.id,
      editConfigs,
      imageUrl,
      encodeCanvasToDataURL,
    ],
  );

  // Selection from child
  const [selectedTextBox, setSelectedTextBox] = useState<TextBox | null>(null);
  // Track whether an image overlay box is selected, to allow handles to spill outside
  const [isImageSelected, setIsImageSelected] = useState<boolean>(false);
  // one-way formatting requests to child
  const [formattingRequest, setFormattingRequest] = useState<
    { token: number; updates: Partial<TextBox> } | undefined
  >(undefined);
  const nextFormattingTokenRef = useRef(0);

  const applyFormatting = (updates: Partial<TextBox>) => {
    nextFormattingTokenRef.current += 1;
    setFormattingRequest({ token: nextFormattingTokenRef.current, updates });
    // Optimistically update toolbar UI so selected state reflects immediately
    setSelectedTextBox((prev) => (prev ? ({ ...prev, ...updates } as TextBox) : prev));
  };

  // Aspect ratios already present or pending (e.g., '9:16') to disable in the Add menu
  // Only consider visible images to allow re-creation when images are removed/undone
  const existingAspectRatios = useMemo(() => {
    const visibleImages = editorMediaList || [];
    const labels = visibleImages
      .map((img: Image) => img?.aspect_ratio)
      .filter((v): v is string => typeof v === "string" && v.length > 0);
    return new Set(labels);
  }, [editorMediaList]);

  // Remove image from visible list and queue delete/hide for finalize
  const handleRemoveImage = useCallback((imageId: string) => {
    if (!imageId) return;
    // Prevent removal of the last remaining media item
    try {
      const currentVisible = Array.isArray(visibleIdsRef.current)
        ? visibleIdsRef.current
        : [];
      if (currentVisible.length <= 1) return;
    } catch {}
    setVisibleImageIds((prev) =>
      (prev || []).filter((v) => String(v) !== String(imageId)),
    );
    // Queue for delete if it was added in this session, else hide
    const idStr = String(imageId);
    const removedImage = polledMediaList.find((im) => im.id === imageId);
    if (removedImage) {
      explicitRemovedImagesRef.current.push(removedImage);
    }
    // Adjust active index if needed
    setActiveImageId((currentId) => {
      const order = (visibleIdsRef.current || []).filter((v) => String(v) !== idStr);
      if (order.length === 0) return null;
      // If we're removing the current active image, switch to the first remaining image
      if (currentId === idStr) {
        return order[0] || null;
      }
      // Otherwise keep the current active image
      return currentId;
    });
  }, []);

  const handleTextBoxesChange = (updatedTextBoxes: TextBox[]) => {
    const currentImageId = String(baseActiveImage?.id || "");
    setEditConfigs((prev) => {
      const previousForImage = prev[currentImageId] || [];
      const nextForImage = updatedTextBoxes || [];
      // Avoid unnecessary state updates if nothing actually changed
      if (previousForImage.length === nextForImage.length) {
        let equal = true;
        for (let i = 0; i < previousForImage.length; i++) {
          const a = previousForImage[i];
          const b = nextForImage[i];
          if (
            a?.id !== b?.id ||
            a?.text !== b?.text ||
            a?.x !== b?.x ||
            a?.y !== b?.y ||
            a?.width !== b?.width ||
            a?.fontFamily !== b?.fontFamily ||
            a?.fontSize !== b?.fontSize ||
            a?.fill !== b?.fill ||
            a?.rotation !== b?.rotation ||
            Boolean(a?.isBold) !== Boolean(b?.isBold) ||
            Boolean(a?.isItalic) !== Boolean(b?.isItalic) ||
            Boolean(a?.isUnderline) !== Boolean(b?.isUnderline) ||
            Boolean(a?.isStrikethrough) !== Boolean(b?.isStrikethrough) ||
            a?.textAlign !== b?.textAlign
          ) {
            equal = false;
            break;
          }
        }
        if (equal) return prev;
      }
      const newConfigs = { ...prev, [currentImageId]: nextForImage };
      return newConfigs;
    });

    // Live-sync toolbar font size while dragging/resizing the selected text box
    if (selectedTextBox) {
      const updatedSelected = updatedTextBoxes.find((tb) => tb.id === selectedTextBox.id);
      if (updatedSelected && updatedSelected.isSelected) {
        const nextSize = Math.round(updatedSelected.fontSize);
        // Update only when it actually changes to avoid extra renders
        if (nextSize !== Math.round(fontSize)) {
          setFontSize(nextSize);
        }
        // Keep local selectedTextBox in sync with latest values
        setSelectedTextBox(updatedSelected);
      }
    }
  };

  // Handle image box changes (per image ID)
  const handleImageBoxesChange = (updated: OverlayImageBox[]) => {
    const currentImageId = String(baseActiveImage?.id || "");
    setImageEditConfigs((prev) => {
      const previousForImage = prev[currentImageId] || [];
      if (previousForImage.length === updated.length) {
        let equal = true;
        for (let i = 0; i < previousForImage.length; i++) {
          const a = previousForImage[i] || ({} as OverlayImageBox);
          const b = updated[i] || ({} as OverlayImageBox);
          if (
            a.id !== b.id ||
            a.x !== b.x ||
            a.y !== b.y ||
            a.width !== b.width ||
            a.height !== b.height ||
            a.rotation !== b.rotation ||
            a.src !== b.src ||
            Boolean(a.isSelected) !== Boolean(b.isSelected)
          ) {
            equal = false;
            break;
          }
        }
        if (equal) return prev;
      }
      return { ...prev, [currentImageId]: updated };
    });
  };

  // Function to detect if there are unsaved text box changes
  const hasUnsavedChanges = useCallback(() => {
    const savedImages = adCreative?.creative?.images;

    const hasTempIds = visibleImageIds.some((id) => id.startsWith("temp-"));
    if (hasTempIds) {
      return false;
    }

    // Check if the number of visible images has changed compared to saved config
    const currentVisibleCount =
      visibleImageIds.filter((id) => !id.startsWith("temp-"))?.length || 0;
    const savedImageCount = adCreative?.creative?.images?.length || 0;

    // If the number of visible images differs from saved images, there are unsaved changes
    if (currentVisibleCount !== savedImageCount) {
      return true;
    }

    // Check if the actual image IDs have changed (even if count is the same)
    const currentVisibleImageIds = visibleImageIds.filter(
      (id) => !id.startsWith("temp-"),
    );
    const savedImageIds = savedImages?.map((img) => img.id) || [];

    // Sort both arrays for comparison
    const sortedCurrentIds = [...currentVisibleImageIds].sort();
    const sortedSavedIds = [...savedImageIds].sort();

    // Check if the sorted arrays are different (detects ID changes even with same count)
    if (sortedCurrentIds.length !== sortedSavedIds.length) {
      return true;
    }

    for (let i = 0; i < sortedCurrentIds.length; i++) {
      if (sortedCurrentIds[i] !== sortedSavedIds[i]) {
        return true;
      }
    }

    // If there is no saved config, only consider it dirty if at least one image has
    // one or more text boxes. Presence of empty arrays should NOT mark dirty.
    if (!savedImages || !savedImages.length) {
      const hasTextBoxes = Object.values(editConfigs).some(
        (boxes) => (boxes?.length ?? 0) > 0,
      );
      const hasImageBoxes = Object.values(imageEditConfigs).some(
        (boxes) => (boxes?.length ?? 0) > 0,
      );
      return hasTextBoxes || hasImageBoxes;
    }

    // Create a map of saved text boxes by image index (v2 normalized fields)
    const savedTextBoxesById = new Map<string, TextBoxPersistedV2[]>();
    const savedImageOverlaysById = new Map<
      string,
      Array<{
        src: string;
        x_norm: number;
        y_norm: number;
        width_norm: number;
        height_norm: number;
        rotation: number;
      }>
    >();

    // Map saved config images to their indices in baseMediaList
    for (let i = 0; i < savedImages.length; i++) {
      const imageId = savedImages[i].id;
      const imConf = savedImages[i].edit_configuration as ImageEditStateV2 | undefined;
      savedTextBoxesById.set(imageId, imConf?.text_boxes || []);
      savedImageOverlaysById.set(imageId, imConf?.image_overlays || []);
    }

    const nearlyEqual = (a: number, b: number, eps = 1e-3): boolean =>
      Math.abs(Math.round((a || 0) * 1000) / 1000 - Math.round((b || 0) * 1000) / 1000) <=
      eps;

    // Check each image for unsaved changes
    for (const currentImage of savedImages) {
      if (!currentImage) continue;
      const imageId = currentImage.id;
      const currentTextBoxes = editConfigs[imageId] || [];
      const currentImageBoxes = imageEditConfigs[imageId] || [];
      const savedTextBoxes = savedTextBoxesById.get(imageId) || [];
      const savedImageOverlays = savedImageOverlaysById.get(imageId) || [];

      // Check if the tracked background image edit differs from the saved background image edit
      // This detects when user has changed the image via magic edit history
      const trackedBackgroundImageEdit = currentBackgroundImageEditByImageId[imageId];
      if (trackedBackgroundImageEdit && currentImage?.background_image_edit) {
        if (trackedBackgroundImageEdit.id !== currentImage.background_image_edit.id) {
          return true; // Image changed via magic edit history
        }
      }

      // For newly added images (not in saved config), mark dirty if any boxes exist
      if (!savedTextBoxesById.has(imageId) && !savedImageOverlaysById.has(imageId)) {
        if ((currentTextBoxes?.length ?? 0) > 0) return true;
        // Also check for image overlays
        if ((currentImageBoxes?.length ?? 0) > 0) return true;
        continue;
      }

      // Compare counts first for text (quick check)
      if (currentTextBoxes.length !== savedTextBoxes.length) return true;
      if (currentImageBoxes.length !== savedImageOverlays.length) return true;

      // Compute display size for normalization of current (display-space → normalized)
      let displayedW = 1,
        displayedH = 1;
      // const naturalW = Math.max(1, image.width || 1),
      // naturalH = Math.max(1, image.height || 1);
      try {
        const key = imageId;
        const cached = scaleCacheRef.current.get(key);
        if (cached) {
          displayedW = Math.max(1, cached.displayedW);
          displayedH = Math.max(1, cached.displayedH);
          // naturalW = Math.max(1, cached.naturalW); // Unused variable
          // naturalH = Math.max(1, cached.naturalH); // Unused variable
        } else {
          const res = getDisplayToNaturalScale({
            image: {
              width: currentImage.width,
              height: currentImage.height,
              aspect_ratio: currentImage.aspect_ratio,
            },
            isActive: imageId === activeImage?.id,
            currentImg: imageId === activeImage?.id ? getCurrentImgRef() : null,
            containerEl: imageRowRef.current,
          });
          displayedW = Math.max(1, res.displayedW);
          displayedH = Math.max(1, res.displayedH);
          // naturalW = Math.max(1, res.naturalW); // Removed unused variable
          // naturalH = Math.max(1, res.naturalH); // Removed unused variable
        }
      } catch {
        // Ignore scale calculation errors
      }

      // Compare text presence
      const currentTexts = currentTextBoxes.map((tb) => tb.text);
      const savedTexts = savedTextBoxes.map((tb: TextBoxPersistedV2) => tb.text);
      if (currentTexts.length !== savedTexts.length) return true;

      // If any text differs, unsaved
      for (let i = 0; i < currentTexts.length; i++) {
        if (String(currentTexts[i] ?? "") !== String(savedTexts[i] ?? "")) return true;
      }

      // Deep compare key fields with tolerance (normalize current display → v2 normalized)
      for (let i = 0; i < currentTextBoxes.length; i++) {
        const cur = currentTextBoxes[i];
        const saved = savedTextBoxes[i];
        if (!saved) return true;
        const q = (v: number) => Math.round((v || 0) * 1000) / 1000;
        const cur_x_norm = q((cur.x || 0) / displayedW);
        const cur_y_norm = q((cur.y || 0) / displayedH);
        const cur_w_norm = q((cur.width || 0) / displayedW);
        const cur_fs_norm = q((cur.fontSize || 0) / displayedH);

        if (!nearlyEqual(cur_x_norm, saved.x_norm)) return true;
        if (!nearlyEqual(cur_y_norm, saved.y_norm)) return true;
        if (!nearlyEqual(cur_w_norm, saved.width_norm)) return true;
        if (!nearlyEqual(cur_fs_norm, saved.font_size_norm)) return true;
        if (Number(cur.rotation || 0) !== Number(saved.rotation || 0)) return true;
        if (String(cur.fontFamily || "").trim() !== String(saved.fontFamily || "").trim())
          return true;
        if (
          String(cur.fill || "").toLowerCase() !== String(saved.fill || "").toLowerCase()
        )
          return true;
        if (Boolean(cur.isBold) !== Boolean(saved.isBold)) return true;
        if (Boolean(cur.isItalic) !== Boolean(saved.isItalic)) return true;
        if (Boolean(cur.isUnderline) !== Boolean(saved.isUnderline)) return true;
        if (Boolean(cur.isStrikethrough) !== Boolean(saved.isStrikethrough)) return true;
        if (String(cur.textAlign || "left") !== String(saved.textAlign || "left"))
          return true;
      }

      // Compare image overlays with tolerance
      for (let i = 0; i < currentImageBoxes.length; i++) {
        const cur = currentImageBoxes[i];
        const saved = savedImageOverlays[i] as {
          src: string;
          x_norm: number;
          y_norm: number;
          width_norm: number;
          height_norm: number;
          rotation: number;
        };
        if (!saved) return true;
        const q = (v: number) => Math.round((v || 0) * 1000) / 1000;
        const cur_x_norm = q((cur.x || 0) / displayedW);
        const cur_y_norm = q((cur.y || 0) / displayedH);
        const cur_w_norm = q((cur.width || 0) / displayedW);
        const cur_h_norm = q((cur.height || 0) / displayedH);
        if (!nearlyEqual(cur_x_norm, saved.x_norm)) return true;
        if (!nearlyEqual(cur_y_norm, saved.y_norm)) return true;
        if (!nearlyEqual(cur_w_norm, saved.width_norm)) return true;
        if (!nearlyEqual(cur_h_norm, saved.height_norm)) return true;
        if (Number(cur.rotation || 0) !== Number(saved.rotation || 0)) return true;
        if (String(cur.src || "") !== String(saved.src || "")) return true;
      }
    }

    return false;
  }, [
    editConfigs,
    imageEditConfigs,
    adCreative,
    activeImage?.id,
    getCurrentImgRef,
    getDisplayToNaturalScale,
    currentBackgroundImageEditByImageId,
    visibleImageIds,
  ]);

  // Handle dialog close with unsaved changes check
  const handleDialogClose = useCallback(
    (open: boolean) => {
      if (!open && hasUnsavedChanges()) {
        // Prevent immediate close and show unsaved changes modal
        setShowUnsavedChangesModal(true);
      } else {
        // No unsaved changes, close normally
        onOpenChange(open, activeImageId || undefined);
      }
    },
    [hasUnsavedChanges, onOpenChange, activeImageId],
  );
  const handleSaveEditConfiguration = useCallback(
    async (options?: { awaitApi?: boolean }): Promise<AdCreative | undefined> => {
      const awaitApi = options?.awaitApi !== false;
      const base = buildEditorStateForSave();
      if (!base) return undefined;

      // Compose image_data for images that currently have text boxes
      const image_data: Record<string, string> = {};
      // Collect permalink_url updates to merge into one creative update
      const updatedImageUrls: Record<string, string> = {};
      // Build a lookup of saved config by image ID to detect changes per image
      const savedImages = currentAdCreativeRef.current?.creative?.images || [];
      const savedById = new Map<string, ImageEditStateV2>();
      for (let i = 0; i < savedImages.length; i++) {
        const img = savedImages[i];
        const imConf = img.edit_configuration as ImageEditStateV2 | undefined;
        if (imConf) {
          savedById.set(img.id, imConf);
        }
      }

      // const nearlyEqual = (a: number, b: number, eps = 1e-4): boolean =>
      //   Math.abs((a || 0) - (b || 0)) <= eps;
      // const normalizeBoxes = (boxes: TextBoxPersistedV2[]) => {
      //   // Sort by id for stable comparison
      //   const copy = [...(boxes || [])].map((b) => ({ ...b }));
      //   copy.sort((a, b) => String(a.id).localeCompare(String(b.id)));
      //   return copy;
      // };
      // const didChange = (
      //   currentIm: ImageEditStateV2,
      //   savedIm?: ImageEditStateV2,
      //   imageId?: string,
      // ): boolean => {
      //   // Check if background_image_edit has changed (e.g., from magic edit)
      //   if (imageId) {
      //     const savedImage = savedImages.find((img) => img.id === imageId);
      //     const trackedBackgroundImageEdit = currentBackgroundImageEditByImageId[imageId];
      //     if (trackedBackgroundImageEdit?.id !== savedImage?.background_image_edit?.id) {
      //       return true;
      //     }
      //   }

      //   // If there is no saved image config, consider it changed if any text or overlays exist
      //   if (!savedIm)
      //     return (
      //       (currentIm.text_boxes || []).length > 0 ||
      //       (currentIm.image_overlays || []).length > 0
      //     );

      //   // Compare text boxes
      //   const a = normalizeBoxes(currentIm.text_boxes || []);
      //   const b = normalizeBoxes(savedIm.text_boxes || []);
      //   if (a.length !== b.length) return true;
      //   for (let i = 0; i < a.length; i++) {
      //     const aa = a[i];
      //     const bb = b[i];
      //     if (String(aa.id) !== String(bb.id)) return true;
      //     if (String(aa.text || "") !== String(bb.text || "")) return true;
      //     if (!nearlyEqual(aa.x_norm, bb.x_norm)) return true;
      //     if (!nearlyEqual(aa.y_norm, bb.y_norm)) return true;
      //     if (!nearlyEqual(aa.width_norm, bb.width_norm)) return true;
      //     if (!nearlyEqual(aa.font_size_norm, bb.font_size_norm)) return true;
      //     if ((aa.rotation || 0) !== (bb.rotation || 0)) return true;
      //     if (String(aa.fontFamily || "") !== String(bb.fontFamily || "")) return true;
      //     if (String(aa.fill || "") !== String(bb.fill || "")) return true;
      //     if (Boolean(aa.isBold) !== Boolean(bb.isBold)) return true;
      //     if (Boolean(aa.isItalic) !== Boolean(bb.isItalic)) return true;
      //     if (Boolean(aa.isUnderline) !== Boolean(bb.isUnderline)) return true;
      //     if (Boolean(aa.isStrikethrough) !== Boolean(bb.isStrikethrough)) return true;
      //     if (String(aa.textAlign || "left") !== String(bb.textAlign || "left"))
      //       return true;
      //   }

      //   // Compare image overlays
      //   const ovA = (currentIm.image_overlays || []).map((o) => ({
      //     src: String(o.src || ""),
      //     x: Math.round(((o.x_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     y: Math.round(((o.y_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     w: Math.round(((o.width_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     h: Math.round(((o.height_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     r: Number(o.rotation || 0),
      //   }));
      //   const ovB = (savedIm.image_overlays || []).map((o) => ({
      //     src: String(o.src || ""),
      //     x: Math.round(((o.x_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     y: Math.round(((o.y_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     w: Math.round(((o.width_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     h: Math.round(((o.height_norm || 0) + Number.EPSILON) * 1000) / 1000,
      //     r: Number(o.rotation || 0),
      //   }));
      //   if (ovA.length !== ovB.length) return true;
      //   // Sort by src+x+y for stable compare
      //   ovA.sort((p, q) => (p.src + p.x + p.y).localeCompare(q.src + q.x + q.y));
      //   ovB.sort((p, q) => (p.src + p.x + p.y).localeCompare(q.src + q.x + q.y));
      //   for (let i = 0; i < ovA.length; i++) {
      //     const aa = ovA[i];
      //     const bb = ovB[i];
      //     if (aa.src !== bb.src) return true;
      //     if (!nearlyEqual(aa.x, bb.x)) return true;
      //     if (!nearlyEqual(aa.y, bb.y)) return true;
      //     if (!nearlyEqual(aa.w, bb.w)) return true;
      //     if (!nearlyEqual(aa.h, bb.h)) return true;
      //     if ((aa.r || 0) !== (bb.r || 0)) return true;
      //   }

      //   return false;
      // };

      // Prefer DOM-based composer for the active image for pixel-identical output
      for (let idx = 0; idx < base.length; idx++) {
        const im = base[idx];
        // Only compose if this image's text boxes changed vs saved config
        // const savedForImage = savedById.get(im.id);
        // const hasChanged = didChange(im.edit_configuration, savedForImage, im.id);
        // if (!hasChanged) continue;
        let dataUrl: string | null = null;
        // Check if this is the active image
        if (im.id === baseActiveImage?.id) {
          dataUrl = await composeActiveToDataURL();
        }
        if (!dataUrl) {
          // Try cache composed when it was last active
          try {
            const id = im.id;
            if (id && composedImageCacheRef.current.has(id)) {
              dataUrl = composedImageCacheRef.current.get(id) || null;
            }
          } catch {
            // Ignore overlay image loading errors
          }
        }
        if (!dataUrl) {
          const currentUrl = currentBackgroundImageEditByImageId[im.id]?.permalink_url;
          dataUrl = await renderImageEditStateToDataURL(
            im.edit_configuration,
            currentUrl,
          );
        }
        if (dataUrl) image_data[String(im.id)] = dataUrl;

        // Set immediate preview using object URL on main image permalink_url
        if (dataUrl) {
          try {
            const url = toObjectUrl(dataUrl);
            // Do not append query to blob: URLs; they must be used as-is
            const cacheBustedUrl = url.startsWith("blob:")
              ? url
              : `${url}?cb=${Date.now()}`;
            const targetId = im?.id;
            if (targetId) {
              // Update local polled map for immediate card preview
              setPolledMediaMap((prev) => {
                const baseIm = prev[targetId];
                const merged = { ...baseIm };
                merged.permalink_url = cacheBustedUrl;
                return { ...prev, [targetId]: merged };
              });
              // Record for a single merged creative update later
              updatedImageUrls[targetId] = cacheBustedUrl;
            }
          } catch (error) {
            console.warn("Failed to create object URL for preview:", error);
          }
        } else {
          console.warn(
            "No data URL available for image composition - canvas may be tainted",
          );
        }
      }
      let updatedCreative: AdCreative | undefined;
      try {
        // Optimistically update the creative once: merge permalink_url updates + edit_configuration
        if (onCreativeUpdate && (currentAdCreativeRef.current || adCreative)) {
          const baseCreative = currentAdCreativeRef.current || (adCreative as AdCreative);
          // Only include visible images in the creative update
          const visibleImages = polledMediaList.filter((im) =>
            visibleImageIds.includes(im.id),
          );

          const updatedImages = visibleImages.map((im) => {
            const baseItem = base.find((item) => item.id === im.id);
            const trackedBackgroundImageEdit = currentBackgroundImageEditByImageId[im.id];
            return updatedImageUrls[im.id]
              ? {
                  ...im,
                  permalink_url: updatedImageUrls[im.id],
                  edit_configuration: baseItem?.edit_configuration,
                  background_image_edit: trackedBackgroundImageEdit || undefined,
                }
              : {
                  ...im,
                  edit_configuration: baseItem?.edit_configuration,
                  background_image_edit: trackedBackgroundImageEdit || undefined,
                };
          });
          updatedCreative = {
            ...baseCreative,
            creative: { ...baseCreative.creative, images: updatedImages },
          } as AdCreative;
          onCreativeUpdate(updatedCreative);
        }
        const req = updateEditConfiguration(
          String(selectedAccount?.id || ""),
          String(adCreative?.id || ""),
          base.map((item) => ({
            ...item,
            base64_data: image_data[item.id] || null,
          })),
        );
        if (awaitApi) {
          await req;
        } else {
          void req.catch((e) => {
            console.error(e);
            toast.error("Failed to save");
          });
        }
        return updatedCreative;
      } catch (e) {
        console.error(e);
        toast.error("Failed to save");
        return updatedCreative;
      }
    },
    [
      selectedAccount?.id,
      adCreative,
      onCreativeUpdate,
      baseActiveImage?.id,
      buildEditorStateForSave,
      composeActiveToDataURL,
      renderImageEditStateToDataURL,
      currentBackgroundImageEditByImageId,
      visibleImageIds,
      polledMediaList,
    ],
  );

  // Unsaved changes modal handlers

  const handleSaveAndContinue = useCallback(async () => {
    // Ensure composition finishes while DOM is still mounted, but don't block on API
    const updatedCreative = await handleSaveEditConfiguration({ awaitApi: false });
    // Close after successful save
    setShowUnsavedChangesModal(false);
    onOpenChange(false, activeImageId || undefined, updatedCreative);
  }, [handleSaveEditConfiguration, onOpenChange, activeImageId]);

  const handleDiscardChanges = useCallback(() => {
    // Just remove grab_text_metadata from ad_creative (optimistic), do not alter in-session overlays (local state only)
    if (currentAdCreativeRef.current) {
      const baseCreative = currentAdCreativeRef.current;
      const updatedImages = (baseCreative.creative.images || []).map((im) => ({
        ...im,
        grab_text_metadata: undefined,
      }));
      currentAdCreativeRef.current = {
        ...baseCreative,
        creative: { ...baseCreative.creative, images: updatedImages },
      };
    }

    // Close the modal and dialog
    setShowUnsavedChangesModal(false);
    onOpenChange(false, activeImageId || undefined);
  }, [onOpenChange, activeImageId]);

  const handleCancelClose = useCallback(() => {
    // Just close the modal, keep the dialog open
    setShowUnsavedChangesModal(false);
  }, []);

  // Reset sizing when active image is not ready/success
  useEffect(() => {
    if (!activeImage || activeImage.status !== "success") {
      // reset sizing when switching to a non-ready image
      setImgSize(null);
      setIsImageReady(false);
    }
  }, [activeImageId, activeImage, setImgSize]);

  // Poll all pending media items until resolved (like pollCreative)
  useEffect(() => {
    const accountId = String(selectedAccount?.id || "");
    if (!accountId) return;
    // Only run regular media polling on Text tab to avoid conflicts with Grab Text flow
    if (activeSidebarTab !== "text") {
      return;
    }
    isPollingEnabledRef.current = true;
    pollTimeoutsRef.current.forEach((timeout) => {
      clearTimeout(timeout);
    });
    scheduledPollIdsRef.current.clear();
    pollTimeoutsRef.current.clear();

    const ensurePolling = (media: Image) => {
      if (media.status !== "pending") return;
      // Skip polling for local placeholders with temp IDs until server returns a real media id
      const mediaId = String(media.id || "");
      if (mediaId.startsWith("temp-")) return;
      if (!visibleImageIds.includes(mediaId)) return;
      // If polling is suppressed due to undo, skip
      if (suppressedPollingIdsRef.current.has(mediaId)) return;
      if (scheduledPollIdsRef.current.has(mediaId)) return;
      if (pollTimeoutsRef.current.has(mediaId)) return;

      const pollOnce = async () => {
        // If polling has been disabled (e.g., dialog closed), exit immediately
        if (!isPollingEnabledRef.current) return;
        try {
          if (pollingInFlightRef.current.has(media.id)) return;
          pollingInFlightRef.current.add(media.id);
          const controller = new AbortController();
          pollControllersRef.current.set(media.id, controller);
          const res = await fetchMedia(accountId, mediaId, { signal: controller.signal });
          const updated = res?.data as unknown as Image;
          if (updated) {
            setPolledMediaMap((prev) => ({ ...prev, [updated.id]: updated }));
            setCurrentBackgroundImageEditByImageId((prev) => ({
              ...prev,
              [updated.id]: updated.background_image_edit || null,
            }));
          }
          if (updated?.status === "pending" && isPollingEnabledRef.current) {
            const t = setTimeout(() => void pollOnce(), 5000);
            pollTimeoutsRef.current.set(media.id, t);
          } else {
            const t = pollTimeoutsRef.current.get(mediaId);
            if (t) clearTimeout(t);
            pollTimeoutsRef.current.delete(mediaId);
            pollControllersRef.current.delete(mediaId);
            // Locally update dashboard with the updated image based on latest creative (local state only)
            if (updated) {
              const baseCreative = currentAdCreativeRef.current;
              if (baseCreative) {
                const updatedImages = (baseCreative.creative.images || []).map((im) =>
                  im.id === updated.id ? updated : im,
                );
                currentAdCreativeRef.current = {
                  ...baseCreative,
                  creative: { ...baseCreative.creative, images: updatedImages },
                };
              }
            }
          }
        } catch {
          if (isPollingEnabledRef.current) {
            const t = setTimeout(() => void pollOnce(), 7000);
            pollTimeoutsRef.current.set(media.id, t);
          }
        } finally {
          pollingInFlightRef.current.delete(mediaId);
        }
      };

      // Mark as scheduled and start immediately
      scheduledPollIdsRef.current.add(mediaId);
      void pollOnce();
    };
    for (const m of baseMediaList || []) ensurePolling(m);
  }, [selectedAccount?.id, baseMediaList, activeSidebarTab, visibleImageIds]);

  // When dialog closes, stop polling and cancel in-flight calls
  useEffect(() => {
    if (open) return;

    isPollingEnabledRef.current = false;
    const timers = new Map(pollTimeoutsRef.current);
    const controllers = new Map(pollControllersRef.current);
    const grabFlowHandles = new Map(grabFlowHandlesRef.current);

    for (const [, t] of timers) clearTimeout(t);
    pollTimeoutsRef.current.clear();
    for (const [, c] of controllers) {
      try {
        c.abort();
      } catch {
        // Ignore abort errors
      }
    }
    pollControllersRef.current.clear();
    pollingInFlightRef.current.clear();
    scheduledPollIdsRef.current.clear();
    explicitRemovedImagesRef.current = [];

    // Also cancel any ongoing grab-text flows for all images
    try {
      for (const [, h] of grabFlowHandles) {
        try {
          h.cancel();
        } catch {
          // Ignore cancel errors
        }
      }
      grabFlowHandlesRef.current.clear();
    } catch {
      // Ignore grab flow cleanup errors
    }
  }, [open]);

  // Safety: also cancel polling if the component unmounts unexpectedly
  useEffect(() => {
    const pollTimeouts = pollTimeoutsRef.current;
    const pollControllers = pollControllersRef.current;
    const pollingInFlight = pollingInFlightRef.current;
    const scheduledPollIds = scheduledPollIdsRef.current;
    const grabFlowHandles = grabFlowHandlesRef.current;

    return () => {
      isPollingEnabledRef.current = false;
      const timers = new Map(pollTimeouts);
      const controllers = new Map(pollControllers);
      const grabFlowHandlesMap = new Map(grabFlowHandles);

      for (const [, t] of timers) clearTimeout(t);
      pollTimeouts.clear();
      for (const [, c] of controllers) {
        try {
          c.abort();
        } catch {
          // Ignore abort errors
        }
      }
      pollControllers.clear();
      pollingInFlight.clear();
      scheduledPollIds.clear();

      // Cancel any grab-text flows on unmount
      try {
        for (const [, h] of grabFlowHandlesMap) {
          try {
            h.cancel();
          } catch {
            // Ignore cancel errors
          }
        }
        grabFlowHandles.clear();
      } catch {
        // Ignore grab flow cleanup errors
      }
    };
  }, []);

  // Persisted Edit Configuration types (v2 - normalized coordinates)
  type TextBoxPersistedV2 = {
    id: string;
    text: string;
    x_norm: number; // 0..1 of image width
    y_norm: number; // 0..1 of image height
    width_norm: number; // 0..1 of image width
    font_size_norm: number; // 0..1 of image height
    rotation: number;
    fontFamily: string;
    fill: string;
    isBold: boolean;
    isItalic: boolean;
    isUnderline: boolean;
    isStrikethrough: boolean;
    textAlign: "left" | "center" | "right";
  };
  type ImageEditStateV2 = {
    aspect_ratio?: string;
    width?: number;
    height?: number;
    original_url?: string;
    current_url?: string; // Current image URL (from magic edit history)
    grab_text?: {
      performed: boolean;
      applied: boolean;
      grab_text_image_id?: string | null;
      metadata?: GrabTextMetadata | null;
    };
    text_boxes: TextBoxPersistedV2[];
    image_overlays?: Array<{
      src: string;
      x_norm: number;
      y_norm: number;
      width_norm: number;
      height_norm: number;
      rotation: number;
    }>;
  };
  type CreativeEditorStateV2 = {
    version: 2;
    account_id: string;
    ad_creative_id: string;
    saved_at: string;
    fonts_used: string[];
    images: ImageEditStateV2[];
  };

  // Hydrate from server-provided images edit_configuration if available (v2 only)
  useEffect(() => {
    // Load edit configuration from images array instead of edit_configuration
    const images = adCreative?.creative?.images || [];
    if (!images?.length || !imageRowRef.current) return;

    // Preload Google Fonts used in overlays
    try {
      // Extract fonts from all image configurations
      const allFonts = images.flatMap((img) => {
        const conf = img.edit_configuration as CreativeEditorStateV2 | undefined;
        return conf?.fonts_used || [];
      });
      allFonts.forEach((f: string) => {
        ensureGoogleFontLoaded(f);
        requestFontFace(f);
      });
    } catch {
      // Ignore font loading errors
    }

    const elem = imageRowRef.current;
    const maxW = Math.max(0, elem.clientWidth) - 8;
    const maxH = Math.max(0, elem.clientHeight) - 8;

    const idToIndex = new Map<string, number>();
    (baseMediaList || []).forEach((im, idx) => idToIndex.set(im.id, idx));

    const next: Record<string, TextBox[]> = {};
    const nextImages: Record<string, OverlayImageBox[]> = {};
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const imConf = img.edit_configuration as ImageEditStateV2 | undefined;
      if (!imConf) continue;

      // Since we removed image_id, we match by index order
      if (i >= (baseMediaList || []).length) continue;
      const idx = i;
      const im = baseMediaList[idx];
      const imageId = String(im.id);
      const naturalW = im.width || imConf.width || 0; // Unused variable
      const naturalH = im.height || imConf.height || 0; // Unused variable

      let scaleX: number, scaleY: number;

      // For the active image, use actual DOM measurements for accuracy
      if (im.id === activeImageId) {
        const currentImg = getCurrentImgRef();
        if (currentImg) {
          // Use actual displayed dimensions from DOM
          const displayedW = currentImg.clientWidth;
          const displayedH = currentImg.clientHeight;
          scaleX = displayedW && naturalW ? displayedW / naturalW : 1;
          scaleY = displayedH && naturalH ? displayedH / naturalH : 1;

          // Calculate actual offsets from DOM positioning
          // const imgRect = currentImg.getBoundingClientRect();
          const containerRect = imageRowRef.current?.getBoundingClientRect();
          if (containerRect) {
            // offsetLeft = Math.round(imgRect.left - containerRect.left);
            // offsetTop = Math.round(imgRect.top - containerRect.top);
          } else {
            // Fallback to center calculation (not currently used)
            // const offsetLeft = Math.round((maxW - displayedW) / 2);
            // const offsetTop = Math.round((maxH - displayedH) / 2);
          }
        } else {
          // Fallback to mathematical approach if DOM ref not available
          const box = computeContainedSizeForRatio(
            im.aspect_ratio || imConf.aspect_ratio,
            maxW,
            maxH,
          ) || { width: naturalW || 1, height: naturalH || 1 };
          // Calculate offset for reference (not currently used)
          // const offsetLeft = Math.round((Math.max(0, maxW) - box.width) / 2);
          // const offsetTop = Math.round((Math.max(0, maxH) - box.height) / 2);
          scaleX = box.width && naturalW ? box.width / naturalW : 1;
          scaleY = box.height && naturalH ? box.height / naturalH : 1;
        }
      } else {
        // For non-active images, use mathematical approach as fallback
        const box = computeContainedSizeForRatio(
          im.aspect_ratio || imConf.aspect_ratio,
          maxW,
          maxH,
        ) || { width: naturalW || 1, height: naturalH || 1 };
        // Calculate offset for reference (not currently used)
        // const offsetLeft = Math.round((Math.max(0, maxW) - box.width) / 2);
        // const offsetTop = Math.round((Math.max(0, maxH) - box.height) / 2);
        scaleX = box.width && naturalW ? box.width / naturalW : 1;
        scaleY = box.height && naturalH ? box.height / naturalH : 1;
      }

      // Track the saved background image edit if available (from magic edit history)
      if (
        img.background_image_edit?.permalink_url &&
        !currentBackgroundImageEditByImageId[im.id]
      ) {
        setCurrentBackgroundImageEditByImageId((prev) => ({
          ...prev,
          [im.id]: img.background_image_edit!,
        }));
      }

      next[imageId] = (imConf.text_boxes || []).map(
        (pb: TextBoxPersistedV2, i: number) => {
          const x_px = (pb.x_norm || 0) * (naturalW || 1);
          const y_px = (pb.y_norm || 0) * (naturalH || 1);
          const w_px = (pb.width_norm || 0) * (naturalW || 1);
          const fs_px = (pb.font_size_norm || 0) * (naturalH || 1);
          return {
            id: pb.id || `tb_${imageId}_${i}`,
            text: pb.text,
            x: x_px * scaleX,
            y: y_px * scaleY,
            width: w_px * scaleX,
            rotation: pb.rotation || 0,
            fontFamily: pb.fontFamily,
            fontSize: fs_px * scaleY,
            fill: pb.fill,
            isBold: !!pb.isBold,
            isItalic: !!pb.isItalic,
            isUnderline: !!pb.isUnderline,
            isStrikethrough: !!pb.isStrikethrough,
            textAlign: pb.textAlign,
            isSelected: false,
            isEditing: false,
          } as TextBox;
        },
      );

      // Hydrate image overlays
      const overlays = (imConf.image_overlays || []).map(
        (ov: {
          src: string;
          x_norm: number;
          y_norm: number;
          width_norm: number;
          height_norm: number;
          rotation: number;
        }) => {
          const x_px = (ov.x_norm || 0) * (naturalW || 1);
          const y_px = (ov.y_norm || 0) * (naturalH || 1);
          const w_px = (ov.width_norm || 0) * (naturalW || 1);
          const h_px = (ov.height_norm || 0) * (naturalH || 1);
          return {
            id: `ib_${imageId}_${Math.random().toString(36).slice(2, 9)}`,
            x: x_px * scaleX,
            y: y_px * scaleY,
            width: w_px * scaleX,
            height: h_px * scaleY,
            rotation: Number(ov.rotation || 0),
            src: String(ov.src || ""),
            isSelected: false,
          } as OverlayImageBox;
        },
      );
      nextImages[imageId] = overlays;
    }
    // Do not overwrite existing in-memory edits; only seed where empty
    setEditConfigs((prev) => {
      const merged: Record<string, TextBox[]> = { ...prev };
      for (const [imageId, value] of Object.entries(next)) {
        const existing = merged[imageId];
        if (!existing || existing.length === 0) {
          merged[imageId] = value;
        }
      }
      return merged;
    });
    setImageEditConfigs((prev) => {
      const merged: Record<string, OverlayImageBox[]> = { ...prev };
      for (const [imageId, value] of Object.entries(nextImages)) {
        const existing = merged[imageId];
        if (!existing || existing.length === 0) {
          merged[imageId] = value;
        }
      }
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreative?.id, baseMediaList, imageRowRef.current]);

  // Compute rendered size to fit image into the image row while preserving aspect ratio
  useLayoutEffect(() => {
    const compute = () => {
      if (!imageRowRef.current) return;
      const elem = imageRowRef.current;
      setContainerSize({
        width: Math.max(0, elem.clientWidth),
        height: Math.max(0, elem.clientHeight),
      });
      if (!imgSize) return;
      const maxW = elem.clientWidth;
      const maxH = elem.clientHeight;
      if (maxW <= 0 || maxH <= 0) return;
      // Update cached scale for the active image
      try {
        const id = activeImage?.id;
        if (id) {
          const s = getDisplayToNaturalScale({
            image: {
              width: activeImage?.width,
              height: activeImage?.height,
              aspect_ratio: activeImage?.aspect_ratio,
            },
            isActive: true,
            currentImg: getCurrentImgRef(),
            containerEl: imageRowRef.current,
          });
          scaleCacheRef.current.set(String(id), s);
        }
      } catch {
        // Ignore scale calculation errors
      }
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [
    imgSize,
    activeImageId,
    activeSidebarTab,
    activeImage?.aspect_ratio,
    activeImage?.height,
    activeImage?.id,
    activeImage?.width,
    getCurrentImgRef,
    getDisplayToNaturalScale,
    displayedUrl,
  ]);

  // Once server-provided images arrive in mediaList, prune matching entries from appendedMedia to avoid duplicates
  useEffect(() => {
    if (!appendedMedia.length) return;
    const serverIds = new Set((mediaList || []).map((m) => m.id));
    if (serverIds.size === 0) return;
    setAppendedMedia((prev) => prev.filter((im) => !serverIds.has(im.id)));
  }, [mediaList, appendedMedia.length]);

  // Container to anchor in-editor modals (over the image area)
  const editorContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog open={open} onOpenChange={handleDialogClose} modal={true}>
      <DialogContent
        className="max-h-[100vh] h-[100vh] max-w-[80vw] bg-transparent border-0 flex flex-col gap-4 shadow-none"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Hidden title for accessibility */}
        <DialogTitle className="sr-only">Edit Image Creative</DialogTitle>
        {/* Unified Container - All Elements in One Box */}
        <div
          ref={editorContainerRef}
          className="flex-1 flex flex-col min-h-0 mb-4 w-full self-center bg-white rounded-[12px] overflow-hidden relative"
        >
          {/* Navbar Section (fixed height to prevent layout shift when toolbar appears) */}
          <div className="h-14 px-2 flex items-center gap-[6px]">
            {/* Undo/Redo buttons */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild className="cursor-pointer ml-0">
                  <button
                    className="h-[32px] w-[32px] ml-0 inline-flex items-center justify-center rounded-[8px] border border-[#E7E8E9] bg-white shadow-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!history.canUndo}
                    onClick={() => {
                      const snap = history.undo();
                      if (snap) applySnapshot(snap);
                    }}
                    aria-label="Undo"
                  >
                    <RiArrowGoBackFill
                      size={14}
                      className={cn(
                        "",
                        history.canUndo
                          ? "cursor-pointer text-brand-primary-800"
                          : "cursor-not-allowed text-brand-primary-new/20",
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{"Undo"}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild className="cursor-pointer ml-0">
                  <button
                    className="h-[32px] w-[32px] ml-0 inline-flex items-center justify-center rounded-[8px] border border-[#E7E8E9] bg-white shadow-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!history.canRedo}
                    onClick={() => {
                      const snap = history.redo();
                      if (snap) applySnapshot(snap);
                    }}
                    aria-label="Redo"
                  >
                    <RiArrowGoForwardFill
                      size={14}
                      className={cn(
                        "",
                        history.canRedo
                          ? "cursor-pointer text-brand-primary-800"
                          : "cursor-not-allowed text-brand-primary-new/20",
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{"Redo"}</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Center Text Toolbar - visible only when a text box is selected and not grabbing or loading */}
            <div className="flex-1 mr-4 flex items-center justify-end">
              {selectedTextBox &&
                !(grabStatesByImageId[activeImageId ?? ""]?.loading ?? false) && (
                  <div className="flex items-center gap-3 rounded-[10px] bg-white border border-border-gray px-2 py-1 shadow-sm max-w-[760px] no-scrollbar">
                    {/* Font selector */}
                    <Button
                      variant="outline"
                      className={`h-8 min-w-[180px] justify-start border border-border-gray hover:bg-gray-50 text-[14px] bg-white transition-all duration-200 shadow-sm ${
                        leftPanel === "font" ? "bg-blue-50 border-blue-500" : ""
                      }`}
                      onClick={() => setLeftPanel(leftPanel === "font" ? null : "font")}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <div
                          className="w-4 h-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-sm flex items-center justify-center text-[10px] text-white font-bold"
                          style={{ fontFamily: selectedFont }}
                        >
                          Aa
                        </div>
                        <span
                          className="truncate max-w-[120px] font-medium"
                          style={{ fontFamily: selectedFont }}
                        >
                          {selectedFont}
                        </span>
                      </div>
                    </Button>

                    {/* Font size controls */}
                    <div
                      className="flex items-center gap-2 border border-border-gray rounded-md px-2 h-8"
                      ref={fontDropdownRef}
                    >
                      <button
                        className="p-1 rounded hover:bg-gray-100"
                        onClick={() => {
                          setFontSize((s) => {
                            const base = Math.round(s);
                            const next = Math.max(1, base - 1);
                            applyFormatting({ fontSize: next });
                            return next;
                          });
                        }}
                      >
                        <IconMinus className="w-4 h-4" />
                      </button>
                      <div className="relative z-[50]">
                        <input
                          value={fontSizeInput}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, "");
                            setFontSizeInput(raw);
                          }}
                          onBlur={() => {
                            const num = Number(fontSizeInput);
                            if (!isFinite(num)) {
                              setFontSizeInput(String(Math.round(fontSize)));
                              setIsFontDropdownOpen(false);
                              return;
                            }
                            const clamped = Math.max(1, Math.min(200, Math.round(num)));
                            setFontSize(clamped);
                            applyFormatting({ fontSize: clamped });
                            setFontSizeInput(String(clamped));
                            setIsFontDropdownOpen(false);
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") {
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                          onMouseDown={() => setIsFontDropdownOpen((prev) => !prev)}
                          className="w-[48px] text-center text-[14px] h-6 rounded-sm outline-none"
                          inputMode="numeric"
                        />
                        {isFontDropdownOpen && (
                          <div
                            className="absolute z-50 mt-1 max-h-[284px] overflow-y-auto min-w-[80px] max-w-[200px] p-1 shadow-none border-none squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
                            role="listbox"
                          >
                            {fontSizeOptions.map((opt) => (
                              <div
                                key={opt.key}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  const clamped = Math.max(
                                    6,
                                    Math.min(200, Number(opt.value)),
                                  );
                                  setFontSize(clamped);
                                  applyFormatting({ fontSize: clamped });
                                  setFontSizeInput(String(clamped));
                                  setIsFontDropdownOpen(false);
                                }}
                                className={`group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px] ${
                                  Math.round(fontSize) === opt.value
                                    ? "bg-selected-gray"
                                    : "hover:bg-hover-gray"
                                }`}
                              >
                                <div className="flex justify-between items-center gap-2">
                                  <div className="w-full flex items-center gap-2">
                                    <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                                      {opt.label}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        className="p-1 rounded hover:bg-gray-100"
                        onClick={() => {
                          setFontSize((s) => {
                            const base = Math.round(s);
                            const next = Math.min(200, base + 1);
                            applyFormatting({ fontSize: next });
                            return next;
                          });
                        }}
                      >
                        <IconPlus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Color picker trigger */}
                    <button
                      className={`relative h-8 w-8 flex items-center justify-center rounded-md border border-border-gray hover:bg-gray-50 transition-all duration-200 ${
                        leftPanel === "color" ? "bg-blue-50 border-blue-500" : ""
                      }`}
                      onClick={() => setLeftPanel(leftPanel === "color" ? null : "color")}
                      aria-label="Text color"
                    >
                      <span className="font-semibold">A</span>
                      <span
                        className="absolute bottom-[4px] left-1 right-1 h-[3px] rounded"
                        style={{ background: selectedColor }}
                      />
                    </button>

                    {/* Styles */}
                    <div className="ml-1 flex items-center gap-1">
                      <button
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 ${selectedTextBox?.isBold ? "bg-gray-100" : ""}`}
                        title="Bold"
                        onClick={() =>
                          applyFormatting({ isBold: !selectedTextBox?.isBold })
                        }
                      >
                        <IconBold className="w-5 h-5" />
                      </button>
                      <button
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 ${selectedTextBox?.isItalic ? "bg-gray-100" : ""}`}
                        title="Italic"
                        onClick={() =>
                          applyFormatting({ isItalic: !selectedTextBox?.isItalic })
                        }
                      >
                        <IconItalic className="w-5 h-5" />
                      </button>
                      <button
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 ${selectedTextBox?.isUnderline ? "bg-gray-100" : ""}`}
                        title="Underline"
                        onClick={() =>
                          applyFormatting({ isUnderline: !selectedTextBox?.isUnderline })
                        }
                      >
                        <IconUnderline className="w-5 h-5" />
                      </button>
                      <button
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 ${selectedTextBox?.isStrikethrough ? "bg-gray-100" : ""}`}
                        title="Strikethrough"
                        onClick={() =>
                          applyFormatting({
                            isStrikethrough: !selectedTextBox?.isStrikethrough,
                          })
                        }
                      >
                        <IconStrikethrough className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Alignment */}
                    <div className="ml-1 flex items-center gap-1">
                      <button
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 ${selectedTextBox?.textAlign === "left" ? "bg-gray-100" : ""}`}
                        title="Align left"
                        onClick={() => applyFormatting({ textAlign: "left" })}
                      >
                        <IconAlignLeft className="w-5 h-5" />
                      </button>
                      <button
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 ${selectedTextBox?.textAlign === "center" ? "bg-gray-100" : ""}`}
                        title="Align center"
                        onClick={() => applyFormatting({ textAlign: "center" })}
                      >
                        <IconAlignCenter className="w-5 h-5" />
                      </button>
                      <button
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 ${selectedTextBox?.textAlign === "right" ? "bg-gray-100" : ""}`}
                        title="Align right"
                        onClick={() => applyFormatting({ textAlign: "right" })}
                      >
                        <IconAlignRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
            </div>

            {/* Save icon button */}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild className="cursor-pointer ml-auto">
                  <button
                    className="ml-0 inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!hasUnsavedChanges() || isSaving}
                    onClick={() => {
                      if (!hasUnsavedChanges() || isSaving) return;
                      setIsSaving(true);
                      void handleSaveEditConfiguration({ awaitApi: true })
                        .catch(() => {})
                        .finally(() => setIsSaving(false));
                    }}
                    aria-label="Save Changes"
                  >
                    <RiSaveLine className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Save Changes</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Download icon button next to Save changes */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild className="cursor-pointer">
                  <button
                    className="ml-0 inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100"
                    onClick={() => {
                      void handleDownloadComposite();
                    }}
                    aria-label="Download edited image"
                  >
                    <RiDownloadLine className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Download</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Cancel editing */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild className="cursor-pointer">
                  <button
                    className="ml-0 inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-100"
                    onClick={() => handleDialogClose(false)}
                    aria-label="Close editor"
                  >
                    <RiCloseLargeLine className="w-5 h-5 text-brand-primary-new" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Horizontal Line */}
          <div className="w-full h-px bg-border-gray"></div>

          {/* Main Content Section */}
          <div className="flex-1 flex min-h-0">
            {/* Split Sidebar Design */}
            <div className="flex">
              {/* First Sidebar - Vertical tabs styled like provided UI */}
              <div className="w-20 bg-[#F5F6FF] border-r border-gray-200 flex flex-col h-full p-[10px] gap-y-2 items-center">
                {/* Text Tab */}
                <button
                  className="group w-full flex flex-col items-center"
                  onClick={() => {
                    handleTabSwitch("text");
                    setIsMaskingEnabled(false);
                  }}
                >
                  <div
                    className={`w-8 h-8 rounded-[8px] grid place-items-center transition-colors p-[4px] bg-white shadow-[0px_0px_0px_1px_rgba(64,87,109,0.04),0px_6px_20px_-4px_rgba(64,87,109,0.3)]`}
                  >
                    <RiImageEditLine
                      className={`w-5 h-5 ${
                        activeSidebarTab === "text"
                          ? "text-[rgb(245,73,39)]" // active → red icon
                          : "text-[#6B7280] group-hover:text-[#4B5563]" // default gray → darker on hover
                      }`}
                    />
                  </div>
                  <div className="mt-[0px] min-h-[22px] flex items-center justify-center text-center">
                    <span className={`text-[11px] font-medium leading-[14px]`}>Edit</span>
                  </div>
                </button>

                {/* Grab Text moved into Text tab options */}

                {/* Erase Tab */}
                <button
                  className="group relative w-full flex flex-col items-center opacity-60 cursor-not-allowed"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  disabled
                  aria-disabled
                  title="Coming soon"
                >
                  <div
                    className={`w-8 h-8 rounded-[8px] grid place-items-center transition-colors p-[4px] ${
                      activeSidebarTab === "erase"
                        ? "bg-[rgb(245,73,39)] text-white"
                        : "bg-transparent text-[#6B7280] group-hover:bg-[#ECEEFF]"
                    }`}
                  >
                    <RiEraserLine className="w-5 h-5" />
                  </div>
                  <div className="mt-[0px] min-h-[22px] flex items-center justify-center text-center">
                    <span className="text-[11px] font-medium leading-[14px] text-[#6B7280]">
                      Erase
                    </span>
                  </div>
                  <div className="pointer-events-none absolute left-[60px] top-1/2 -translate-y-1/2 rounded-md bg-[#111827] text-white text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow">
                    Coming soon
                  </div>
                </button>
              </div>

              {/* Second Sidebar - Content based on selected tab */}
              <div className="w-[250px] bg-white border-r border-gray-200">
                {activeSidebarTab === "text" && !isMagicEditActive && (
                  <div className="flex flex-col h-full">
                    {/* Grab Text states inside Text tab (ABOVE menu) */}
                    {(grabStatesByImageId[activeImageId ?? ""]?.isActive ?? false) &&
                    (grabStatesByImageId[activeImageId ?? ""]?.loading ?? false) ? (
                      <div className="p-4 flex-shrink-0">
                        <div className="w-full max-w-[200px] mb-4">
                          <div className="relative">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-teal-500 h-2 rounded-full transition-all duration-300 ease-out"
                                style={{
                                  width: `${Math.round(grabStatesByImageId[activeImageId ?? ""]?.progress ?? 0)}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="text-center text-gray-700 mb-4">
                          <div className="font-medium mb-1">
                            Extracting text from image...
                          </div>
                          <div className="text-sm text-gray-500">
                            Please wait while we process your image.
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => cancelGrabText(activeImageId || undefined)}
                          className="w-full"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (grabStatesByImageId[activeImageId ?? ""]?.isActive ?? false) &&
                      (grabStatesByImageId[activeImageId ?? ""]?.success ?? false) ? (
                      <div className="p-4 flex-shrink-0">
                        <div className="text-center text-gray-700 mb-4">
                          <div className="font-medium mb-1">
                            Text extraction completed
                          </div>
                          <div className="text-sm text-gray-500">
                            Preview the extracted text. Use Cancel to revert or Grab to
                            apply changes.
                          </div>
                        </div>
                        <div className="w-full space-y-2">
                          <Button
                            variant="outline"
                            onClick={handleCancelGrabText}
                            className="w-full justify-center"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleConfirmGrabText}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white justify-center"
                          >
                            Grab
                          </Button>
                        </div>
                      </div>
                    ) : (grabStatesByImageId[activeImageId ?? ""]?.alreadyGrabbed ??
                      false) ? (
                      <div className="p-4 flex-shrink-0">
                        <div className="text-center text-gray-700 mb-4">
                          <div className="font-medium mb-1">
                            Text already extracted for this image
                          </div>
                          <div className="text-sm text-gray-500">
                            Showing current text boxes; you can adjust them in the editor.
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          onClick={handleBackToTextFromGrab}
                          className="w-full"
                        >
                          Back to Editor
                        </Button>
                      </div>
                    ) : null}

                    {/* Fixed header section with button - only when idle */}
                    {!(
                      (grabStatesByImageId[activeImageId ?? ""]?.loading ?? false) ||
                      (grabStatesByImageId[activeImageId ?? ""]?.success ?? false) ||
                      (grabStatesByImageId[activeImageId ?? ""]?.alreadyGrabbed ?? false)
                    ) && (
                      <div className="p-4 flex-shrink-0">
                        {/* <Button
                          className={
                            "mb-2 flex items-center gap-2 text-sm justify-start h-10 border-border-gray hover:bg-gray-50 w-full " +
                            (isCurrentImageProcessing
                              ? "opacity-60 cursor-not-allowed pointer-events-none"
                              : "")
                          }
                          variant="outline"
                          disabled={isCurrentImageProcessing}
                          onClick={() => {
                            // Stay on text tab; just trigger grab-text flow
                            // Deselect any selected text box to hide the text toolbar
                            setDeselectToken((t) => t + 1);
                            void handleGrabText();
                          }}
                        >
                          <RiFontSizeAi className="w-4 h-4" />
                          <span
                            className={
                              "text-[12px] font-medium leading-[19.16px] tracking-[0.02em] text-brand-primary-800"
                            }
                          >
                            Grab Text
                          </span>
                        </Button> */}
                        <Button
                          className={`mb-2 flex items-center gap-2 text-sm justify-start h-10 border-border-gray hover:bg-gray-50 w-full ${
                            isTextBoxMode
                              ? "border-blue-500 text-blue-600 bg-blue-50"
                              : ""
                          }`}
                          variant="outline"
                          onClick={() => {
                            // Immediately add a text box at center with current font
                            setAddNowToken((t) => t + 1);
                            // Turn off masking if active
                            if (isMaskingEnabled) {
                              setIsMaskingEnabled(false);
                            }
                            // Ensure add mode is off after adding
                            setIsTextBoxMode(false);
                            // Clear previous selection so click does not inadvertently select+add
                            setSelectedTextBox(null);
                          }}
                        >
                          <RiText className="w-4 h-4" />
                          <span
                            className={`text-[12px] font-medium leading-[19.16px] tracking-[0.02em] ${
                              isTextBoxMode ? "text-blue-600" : "text-brand-primary-800"
                            }`}
                          >
                            Add a text box
                          </span>
                        </Button>
                        <Button
                          className={`mb-2 flex items-center gap-2 text-sm justify-start h-10 border-border-gray hover:bg-gray-50 w-full ${
                            isMagicEditActive
                              ? "border-blue-500 text-blue-600 bg-blue-50"
                              : ""
                          }`}
                          variant="outline"
                          onClick={() => {
                            // Toggle magic edit mode and deselect text boxes
                            setDeselectToken((t) => t + 1);
                            handleMagicEditToggle(!isMagicEditActive);
                          }}
                        >
                          <RiSparkling2Line className="w-4 h-4" />
                          <span
                            className={`text-[12px] font-medium leading-[19.16px] tracking-[0.02em] ${
                              isMagicEditActive
                                ? "text-blue-600"
                                : "text-brand-primary-800"
                            }`}
                          >
                            Magic Edit
                          </span>
                        </Button>
                        <input
                          ref={imageFileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                              const src = (reader.result as string) || "";
                              const img = new Image();
                              img.onload = () => {
                                setNextAddImageToken((t) => t + 1);
                                setImageAddRequest({
                                  token: nextAddImageToken + 1,
                                  src,
                                  naturalWidth: img.naturalWidth || 0,
                                  naturalHeight: img.naturalHeight || 0,
                                });
                              };
                              img.src = src;
                            };
                            reader.readAsDataURL(file);
                            // reset input value so the same file can be picked again
                            e.currentTarget.value = "";
                          }}
                        />
                        {/* Brand logo library (always visible) */}
                        <BrandLogoLibrary
                          onImageSelect={(params) => {
                            setImageAddRequest({
                              token: params.token,
                              src: params.src,
                              naturalWidth: params.naturalWidth,
                              naturalHeight: params.naturalHeight,
                            });
                          }}
                          nextAddImageToken={nextAddImageToken}
                          setNextAddImageToken={setNextAddImageToken}
                        />
                        {/* Creative library gallery (always visible) */}
                        <CreativeLibraryGallery
                          creativeLibrary={creativeLibrary}
                          setCreativeLibrary={setCreativeLibrary}
                          isLoadingCreativeLibrary={isLoadingCreativeLibrary}
                          onImageSelect={(params) => {
                            setImageAddRequest({
                              token: params.token,
                              src: params.src,
                              naturalWidth: params.naturalWidth,
                              naturalHeight: params.naturalHeight,
                            });
                          }}
                          nextAddImageToken={nextAddImageToken}
                          setNextAddImageToken={setNextAddImageToken}
                        />
                      </div>
                    )}
                  </div>
                )}

                {activeSidebarTab === "text" && isMagicEditActive && (
                  <MagicEdit
                    activeImageId={activeImage?.id}
                    currentBackgroundImageEdit={
                      currentBackgroundImageEditByImageId[activeImage?.id || ""] ||
                      undefined
                    }
                    magicEditState={magicEditStateByImageId[activeImage?.id || ""]}
                    onStateChange={(stateOrUpdater) => {
                      const imgId = activeImage?.id;
                      if (imgId) {
                        setMagicEditStateByImageId((prev) => {
                          const currentState = prev[imgId] || {
                            prompt: "",
                            selectedReferenceImageId: null,
                            pollingImageId: null,
                            isProcessing: false,
                            baseImageId: imgId,
                            history: [],
                            isLoadingHistory: false,
                            isLoadingMore: false,
                            nextCursor: null,
                            hasMore: false,
                          };
                          return {
                            ...prev,
                            [imgId]:
                              typeof stateOrUpdater === "function"
                                ? stateOrUpdater(currentState)
                                : stateOrUpdater,
                          };
                        });
                      }
                    }}
                    onEdit={(editResult) => {
                      // When a new magic edit is completed, update the current image to show the result
                      if (editResult.permalink_url && activeImage?.id) {
                        // Track the background image edit object for this image
                        setCurrentBackgroundImageEditByImageId((prev) => ({
                          ...prev,
                          [activeImage.id]: editResult,
                        }));

                        // Update the current image's URL to show the new magic edit result
                        setPolledMediaMap((prev) => {
                          const currentImage = prev[activeImage.id];
                          if (currentImage) {
                            return {
                              ...prev,
                              [activeImage.id]: {
                                ...currentImage,
                                background_image_edit: editResult,
                              },
                            };
                          }
                          return prev;
                        });
                      }
                    }}
                    onImageSelect={(selectedImage) => {
                      // Track the background image edit object for this image
                      if (selectedImage.permalink_url && activeImage?.id) {
                        setCurrentBackgroundImageEditByImageId((prev) => ({
                          ...prev,
                          [activeImage.id]: selectedImage,
                        }));
                        setPolledMediaMap((prev) => ({
                          ...prev,
                          [activeImage.id]: {
                            ...prev[activeImage.id],
                            background_image_edit: selectedImage,
                          },
                        }));
                      }
                    }}
                    onStatusChange={(imageId, status) => {
                      // Update magic edit processing state instead of media status
                      // This prevents the image from reloading when status changes
                      if (status === "pending") {
                        setMagicEditProcessingByImageId((prev) => ({
                          ...prev,
                          [imageId]: true,
                        }));
                      } else {
                        setMagicEditProcessingByImageId((prev) => {
                          const newState = { ...prev };
                          delete newState[imageId];
                          return newState;
                        });
                      }
                    }}
                    onClose={() => {
                      handleMagicEditToggle(false);
                    }}
                  />
                )}

                {activeSidebarTab !== "text" &&
                  activeSidebarTab !== "background-ai" &&
                  activeSidebarTab !== "library" && (
                    <div className="bg-black text-white h-full flex flex-col">
                      <div className="p-4 flex-1 flex items-center justify-center">
                        <div className="text-center text-gray-300">
                          {activeSidebarTab === "erase" &&
                            (isMaskingEnabled
                              ? "Brush to remove objects"
                              : "Erase feature coming soon")}
                          {activeSidebarTab === "magic-generate" &&
                            "Magic Generate feature coming soon"}
                        </div>
                      </div>
                      <div className="p-4 border-t border-gray-700">
                        <Button
                          variant="outline"
                          className="w-full text-white border-gray-600 hover:bg-gray-800"
                          onClick={() => {
                            handleTabSwitch("text");
                            setIsMaskingEnabled(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Vertical Line */}
            <div className="w-px bg-border-gray"></div>

            {/* Image Preview Section (Editor area) */}
            <div
              ref={previewRef}
              className="flex-1 flex flex-col p-4 overflow-hidden relative"
              style={{ backgroundColor: "#f0f1f5" }}
            >
              {/* Image row fills available space */}
              <div
                ref={imageRowRef}
                className="flex-1 min-h-0 flex items-center justify-center"
                onMouseDown={() => {
                  if (isInteracting) return;
                  setDeselectToken((t) => t + 1);
                  setImageDeselectToken((t) => t + 1);
                }}
              >
                {!containerSize ? null : baseActiveImage?.status === "pending" ? (
                  (() => {
                    const rectW = containerSize.width - 8;
                    const rectH = containerSize.height - 8;
                    const ar = activeImage?.aspect_ratio;
                    const size = computeContainedSizeForRatio(ar, rectW, rectH);
                    return (
                      <div
                        className="relative"
                        style={{
                          width: `${size?.width ?? rectW}px`,
                          height: `${size?.height ?? rectH}px`,
                          overflow:
                            selectedTextBox || isImageSelected || isInteracting
                              ? ("visible" as const)
                              : ("hidden" as const),
                        }}
                      >
                        <AdCard
                          className="rounded-[0px] w-full h-full border-1"
                          body={{
                            media: {
                              className:
                                "h-full border-0 rounded-[0px] [mask-image:none]",
                              source: imageUrl,
                              type: "image",
                              minLoadingHeight: size?.height ?? rectH,
                            },
                          }}
                          status={activeImage?.status}
                        />
                      </div>
                    );
                  })()
                ) : !containerSize ? null : (grabStatesByImageId[
                    baseActiveImage?.id ?? ""
                  ]?.loading ??
                    false) &&
                  activeImage ? (
                  <div
                    className="relative overflow-hidden"
                    style={{
                      width: `${(() => {
                        const rectW = containerSize.width - 8;
                        const rectH = containerSize.height - 8;
                        const ar = activeImage?.aspect_ratio;
                        const size = computeContainedSizeForRatio(ar, rectW, rectH);
                        return size?.width ?? rectW;
                      })()}px`,
                      height: `${(() => {
                        const rectW = containerSize.width - 8;
                        const rectH = containerSize.height - 8;
                        const ar = activeImage?.aspect_ratio;
                        const size = computeContainedSizeForRatio(ar, rectW, rectH);
                        return size?.height ?? rectH;
                      })()}px`,
                      overflow:
                        selectedTextBox || isImageSelected || isInteracting
                          ? ("visible" as const)
                          : ("hidden" as const),
                    }}
                  >
                    <AdCard
                      className="rounded-[0px] w-full h-full border-1"
                      body={{
                        media: {
                          className: "h-full border-0 rounded-[0px] [mask-image:none]",
                          source: imageUrl,
                          type: "image",
                        },
                      }}
                      status="pending"
                    />
                  </div>
                ) : !containerSize ? null : imageUrl ? (
                  <div
                    className="relative overflow-hidden"
                    style={{
                      width: `${(() => {
                        const rectW = containerSize.width - 8;
                        const rectH = containerSize.height - 8;
                        const ar = activeImage?.aspect_ratio;
                        const size = computeContainedSizeForRatio(ar, rectW, rectH);
                        return size?.width ?? rectW;
                      })()}px`,
                      height: `${(() => {
                        const rectW = containerSize.width - 8;
                        const rectH = containerSize.height - 8;
                        const ar = activeImage?.aspect_ratio;
                        const size = computeContainedSizeForRatio(ar, rectW, rectH);
                        return size?.height ?? rectH;
                      })()}px`,
                      overflow:
                        selectedTextBox || isImageSelected || isInteracting
                          ? ("visible" as const)
                          : ("hidden" as const),
                    }}
                  >
                    {displayedUrl ? (
                      <img
                        ref={(el) => {
                          if (activeImage?.id) {
                            setImgRef(activeImage.id, el);
                          }
                        }}
                        src={displayedUrl || ""}
                        crossOrigin="anonymous"
                        alt="Creative to edit"
                        className="block h-full w-full object-contain select-none"
                        style={{ opacity: isImageReady ? 1 : 0 }}
                        draggable={false}
                        onLoad={(e) => {
                          if (displayedUrl) {
                            try {
                              loadedImageUrlsRef.current.add(displayedUrl);
                            } catch {
                              // Ignore overlay image loading errors
                            }
                          }
                          setImgSize({
                            width: e.currentTarget.naturalWidth,
                            height: e.currentTarget.naturalHeight,
                          });
                          // Ensure loader overlay hides if preloading failed and we rely on <img> load
                          setIsImageReady(true);
                        }}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                      />
                    ) : null}
                    {!isImageReady && (
                      <div className="absolute inset-0 grid place-items-center z-[50]">
                        <DiagonalShimmer
                          key="success-transition-general"
                          colors={["#F6F6F6", "#BFBFBF", "#FFFFFF"]}
                          duration={3}
                        />
                      </div>
                    )}
                    {isCurrentImageProcessing && (
                      <div className="h-full w-full absolute inset-0 grid place-items-center z-[50] pointer-events-none">
                        <DiagonalShimmer
                          key="preloading-pending-transition"
                          backgroundSize="400% 200%"
                          duration={5}
                          gradient="linear-gradient(-45deg, rgba(255,91,26,0) 0%, rgba(255,91,26,0) 35%, #FF5B1A33 49%, #FF517333 51%, rgba(255,81,115,0) 65%, rgba(255,81,115,0) 100%)"
                        />
                      </div>
                    )}
                    {/* Canvas overlay is present but invisible & non-interactive when masking is OFF */}
                    {imgSize && (
                      <canvas
                        ref={canvasRef}
                        width={imgSize.width}
                        height={imgSize.height}
                        className="absolute inset-0 w-full h-full"
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={finishStroke}
                        onPointerLeave={finishStroke}
                        style={{
                          opacity: isMaskingEnabled ? 1 : 0,
                          pointerEvents: isMaskingEnabled ? "auto" : "none",
                          cursor: isMaskingEnabled
                            ? `url('data:image/svg+xml,<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><foreignObject x="-4" y="-4" width="56" height="56"><div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(2px);clip-path:url(%23bgblur_0_34322_55792_clip_path);height:100%;width:100%"></div></foreignObject><circle data-figma-bg-blur-radius="4" cx="24" cy="24" r="24" fill="%23E3E3E3" fill-opacity="0.4"/><defs><clipPath id="bgblur_0_34322_55792_clip_path" transform="translate(4 4)"><circle cx="24" cy="24" r="24"/></clipPath></defs></svg>') 24 24, auto`
                            : "default",
                        }}
                      />
                    )}

                    {/* Image Boxes Overlay (hide whenever grab overlay is shown) */}
                    {!(
                      (grabStatesByImageId[baseActiveImage?.id ?? ""]?.isActive ??
                        false) ||
                      ((grabStatesByImageId[baseActiveImage?.id ?? ""]?.success ??
                        false) &&
                        Boolean(
                          grabStatesByImageId[baseActiveImage?.id ?? ""]?.tempMetadata,
                        )) ||
                      (grabStatesByImageId[baseActiveImage?.id ?? ""]?.alreadyGrabbed ??
                        false)
                    ) && (
                      <KonvaImageBox
                        key={`img-overlay-${activeImageId}`}
                        imageRef={currentImgRefObject}
                        onBoxesChange={handleImageBoxesChange}
                        onSelectionChange={(imgSel) => {
                          // When an image box is selected, deselect text boxes; when cleared, keep as is
                          if (imgSel) setDeselectToken((t) => t + 1);
                          setIsImageSelected(Boolean(imgSel));
                        }}
                        onInteractionStateChange={(active) => setIsInteracting(active)}
                        initialImageBoxes={
                          imageEditConfigs[String(baseActiveImage?.id || "")] || []
                        }
                        deselectRequestToken={imageDeselectToken}
                        addImageRequest={imageAddRequest}
                        addOnNextClick={imageAddRequest}
                        onConsumeAddOnNextClick={() => setImageAddRequest(null)}
                        peerTextBoxes={(
                          editConfigs[String(baseActiveImage?.id || "")] || []
                        ).map((tb) => ({
                          id: tb.id,
                          x: tb.x,
                          y: tb.y,
                          width: tb.width,
                          height: Math.max(10, Math.round((tb.fontSize || 14) * 1.2)),
                          rotation: tb.rotation || 0,
                        }))}
                      />
                    )}

                    {/* Grab Text Preview Overlay - show extracted text boxes in preview */}
                    {(() => {
                      const s = grabStatesByImageId[baseActiveImage?.id ?? ""];
                      const hasActiveSuccess = Boolean(
                        s?.isActive && s?.success && s?.tempMetadata,
                      );
                      const clientBoxesLen = Number(
                        polledMediaList?.find((img) => img.id === activeImageId)
                          ?.client_grabbed_image?.grab_text_metadata?.text_metadata
                          ?.text_boxes?.length || 0,
                      );
                      const serverBoxesLen = Number(
                        polledMediaList?.find((img) => img.id === activeImageId)
                          ?.grab_text_metadata?.text_metadata?.text_boxes?.length || 0,
                      );
                      const hasAlreadyMetadata = Boolean(
                        (s?.alreadyGrabbed ?? false) &&
                          (clientBoxesLen > 0 || serverBoxesLen > 0),
                      );
                      return (
                        Boolean(isImageReady) &&
                        Boolean(imgSize) &&
                        (hasActiveSuccess || hasAlreadyMetadata)
                      );
                    })() ? (
                      <div className="absolute inset-0 w-full h-full pointer-events-none">
                        {(() => {
                          const s = grabStatesByImageId[baseActiveImage?.id ?? ""];
                          const tempBoxes = s?.tempMetadata?.text_metadata?.text_boxes;
                          const clientBoxes = polledMediaList?.find(
                            (img) => img.id === activeImageId,
                          )?.client_grabbed_image?.grab_text_metadata?.text_metadata
                            ?.text_boxes;
                          const serverBoxes = polledMediaList?.find(
                            (img) => img.id === activeImageId,
                          )?.grab_text_metadata?.text_metadata?.text_boxes;
                          return tempBoxes ?? clientBoxes ?? serverBoxes ?? [];
                        })().map((textBox: GrabTextBox, index: number) => {
                          // Calculate scaled position and size based on image dimensions
                          const safeImgSize = imgSize ?? { width: 1, height: 1 };
                          const currentImg = getCurrentImgRef();
                          const scaleX = currentImg
                            ? currentImg.clientWidth / safeImgSize.width
                            : 1;
                          const scaleY = currentImg
                            ? currentImg.clientHeight / safeImgSize.height
                            : 1;

                          const scaledX = textBox.x * scaleX;
                          const scaledY = textBox.y * scaleY;
                          const scaledWidth = textBox.width * scaleX;
                          const scaledHeight = textBox.height * scaleY;

                          // Convert font weight to CSS font-weight
                          const fontWeight =
                            textBox.font_characteristics?.weight === "thin" ||
                            textBox.font_characteristics?.weight === "light" ||
                            textBox.font_characteristics?.weight === "regular"
                              ? "normal"
                              : "bold";

                          // Calculate optimal font size that fits within the box for Lato
                          const calculateOptimalFontSize = (
                            text: string,
                            width: number,
                            height: number,
                          ): number => {
                            let fontSize = Math.min(height * 0.5, width * 0.1);
                            const maxFontSize = height * 0.8;
                            const minFontSize = 8;
                            const measureElement = document.createElement("div");
                            measureElement.style.position = "absolute";
                            measureElement.style.visibility = "hidden";
                            measureElement.style.whiteSpace = "pre-wrap";
                            measureElement.style.wordBreak = "break-word";
                            measureElement.style.lineHeight = "1.2";
                            measureElement.style.fontFamily = "Lato";
                            measureElement.style.fontWeight = fontWeight;
                            measureElement.style.fontStyle =
                              textBox.font_characteristics?.style || "normal";
                            measureElement.style.width = `${width - 4}px`;
                            measureElement.style.padding = "2px";
                            measureElement.textContent = text;
                            document.body.appendChild(measureElement);
                            let low = minFontSize;
                            let high = maxFontSize;
                            let bestFit = minFontSize;
                            while (low <= high) {
                              fontSize = (low + high) / 2;
                              measureElement.style.fontSize = `${fontSize}px`;
                              const rect = measureElement.getBoundingClientRect();
                              const textHeight = rect.height;
                              if (textHeight <= height - 4) {
                                bestFit = fontSize;
                                low = fontSize + 0.5;
                              } else {
                                high = fontSize - 0.5;
                              }
                            }
                            document.body.removeChild(measureElement);
                            return Math.max(minFontSize, Math.min(maxFontSize, bestFit));
                          };

                          const optimalFontSize = calculateOptimalFontSize(
                            textBox.text,
                            scaledWidth,
                            scaledHeight,
                          );

                          return (
                            <div
                              key={index}
                              className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-auto cursor-pointer"
                              style={{
                                left: scaledX,
                                top: scaledY,
                                width: scaledWidth,
                                height: scaledHeight,
                                color: `rgb(${textBox.text_color?.[0]}, ${textBox.text_color?.[1]}, ${textBox.text_color?.[2]})`,
                                textAlign: textBox.text_alignment,
                                fontStyle: textBox.font_characteristics?.style,
                                fontWeight: fontWeight,
                                fontSize: `${optimalFontSize}px`,
                                fontFamily: "Lato, system-ui, -apple-system, sans-serif",
                                padding: "2px",
                                overflow: "hidden",
                                wordBreak: "break-word",
                                lineHeight: "1.2",
                                whiteSpace: "pre-wrap",
                                display: "flex",
                                alignItems:
                                  textBox.text_alignment === "center"
                                    ? "center"
                                    : "flex-start",
                                justifyContent:
                                  textBox.text_alignment === "center"
                                    ? "center"
                                    : textBox.text_alignment === "right"
                                      ? "flex-end"
                                      : "flex-start",
                              }}
                              title={`Extracted text: ${textBox.text}`}
                            >
                              <span
                                style={{
                                  textAlign: textBox.text_alignment,
                                  width: "100%",
                                }}
                              >
                                {textBox.text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    {/* Text Boxes Overlay (hide whenever grab overlay is shown) */}
                    {!(
                      (grabStatesByImageId[baseActiveImage?.id ?? ""]?.isActive ??
                        false) ||
                      ((grabStatesByImageId[baseActiveImage?.id ?? ""]?.success ??
                        false) &&
                        Boolean(
                          grabStatesByImageId[baseActiveImage?.id ?? ""]?.tempMetadata,
                        )) ||
                      (grabStatesByImageId[baseActiveImage?.id ?? ""]?.alreadyGrabbed ??
                        false)
                    ) &&
                      Boolean(isImageReady) && (
                        <KonvaTextBox
                          key={activeImageId}
                          imageRef={currentImgRefObject}
                          onTextBoxesChange={handleTextBoxesChange}
                          onSelectionChange={(tb) => {
                            setSelectedTextBox(tb);
                            // When a text box is selected, ensure image boxes are deselected
                            if (tb) setImageDeselectToken((t) => t + 1);
                            if (tb) {
                              setSelectedFont(tb.fontFamily);
                              setFontSize(Math.round(tb.fontSize));
                              setSelectedColor(tb.fill);
                              setCustomColorValue(tb.fill.toUpperCase());
                            } else {
                              setLeftPanel(null);
                            }
                          }}
                          isAddingText={isTextBoxMode}
                          onToggleAddingText={() => setIsTextBoxMode(false)}
                          addNowToken={addNowToken}
                          formattingRequest={formattingRequest}
                          deselectRequestToken={deselectToken}
                          onInteractionStateChange={(active) => setIsInteracting(active)}
                          initialTextBoxes={
                            editConfigs[String(baseActiveImage?.id || "")] || []
                          }
                          defaultFont={{
                            fontFamily: selectedFont,
                            fontSize: fontSize,
                            fill: selectedColor,
                          }}
                          peerImageBoxes={(
                            imageEditConfigs[String(baseActiveImage?.id || "")] || []
                          ).map((b) => ({
                            id: b.id,
                            x: b.x,
                            y: b.y,
                            width: b.width,
                            height: b.height,
                            rotation: b.rotation || 0,
                          }))}
                        />
                      )}
                  </div>
                ) : !containerSize ? null : (
                  <div
                    className="relative overflow-hidden grid place-items-center"
                    style={{ height: containerSize.height, width: containerSize.width }}
                  >
                    {(() => {
                      const ar = mediaList?.find(
                        (img) => img.id === activeImageId,
                      )?.aspect_ratio;
                      const size = computeContainedSizeForRatio(
                        ar,
                        containerSize.width - 8,
                        containerSize.height - 8,
                      );
                      const w = size?.width ?? containerSize.width - 8;
                      const h = size?.height ?? containerSize.height - 8;
                      return (
                        <div
                          style={{ width: w, height: h }}
                          className="relative overflow-hidden rounded-md"
                        >
                          <DiagonalShimmer
                            backgroundSize="400% 200%"
                            duration={5}
                            gradient="linear-gradient(-45deg, rgba(255,91,26,0) 0%, rgba(255,91,26,0) 35%, #FF5B1A33 49%, #FF517333 51%, rgba(255,81,115,0) 65%, rgba(255,81,115,0) 100%)"
                          />
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Image selector buttons inside Image Preview Section */}
              {editorMediaList && editorMediaList.length > 0 ? (
                <div
                  ref={thumbsRef}
                  className="mt-3 w-full flex items-center justify-center"
                >
                  <div className="flex items-center gap-2 z-[50]">
                    {editorMediaList.map((img, idx) => {
                      const thumb =
                        img.background_image_edit?.permalink_url || img.permalink_url;
                      const isPendingThumb = img.status === "pending" || !thumb;
                      const ratioLabel = img?.aspect_ratio;
                      const isLast = (editorMediaList?.length || 0) <= 1;
                      return (
                        <div
                          key={img.id}
                          className={`group relative w-12 h-12 rounded border bg-white ${img.id === activeImageId ? "border-[1.5] border-[rgb(245,73,39)]" : "border-gray-300 hover:border-gray-400"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTextBox(null);
                            setDeselectToken((t) => t + 1);
                            setImageDeselectToken((t) => t + 1);
                            // Fire-and-forget composition of the previous image to avoid blocking switch
                            try {
                              const prevId = activeImageId;
                              const prev = editorMediaList?.find(
                                (img) => img.id === prevId,
                              );
                              if (prev && prev?.id != null) {
                                void (async () => {
                                  const composed = await composeActiveToDataURL({
                                    fast: true,
                                  });
                                  if (composed)
                                    composedImageCacheRef.current.set(
                                      String(prev.id),
                                      composed,
                                    );
                                })();
                              }
                            } catch {
                              // Ignore overlay image loading errors
                            }
                            switchStartRef.current =
                              typeof performance !== "undefined"
                                ? performance.now()
                                : Date.now();
                            // switching is a history-worthy action (tracked via activeId in snapshot)
                            setActiveImageId(img.id);
                          }}
                          aria-label={`Show image ${idx + 1}`}
                        >
                          {isPendingThumb ? (
                            <div className="w-full h-full relative grid place-items-center bg-white cursor-pointer rounded overflow-hidden">
                              {(() => {
                                const size = computeContainedSizeForRatio(
                                  ratioLabel,
                                  48,
                                  48,
                                );
                                const w = size?.width ?? 44;
                                const h = size?.height ?? 44;
                                return (
                                  <div
                                    style={{ width: `${w}px`, height: `${h}px` }}
                                    className="overflow-hidden cursor-pointer"
                                  >
                                    <AdCard
                                      className="rounded-[0px] w-full h-full border-1 cursor-pointer"
                                      body={{
                                        media: {
                                          source:
                                            img.background_image_edit?.permalink_url ||
                                            img.permalink_url,
                                          type: "image",
                                          className:
                                            "h-full border-0 rounded-[0px] [mask-image:none]",
                                        },
                                      }}
                                      status={img?.status}
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          ) : thumb ? (
                            <img
                              src={thumb}
                              alt={`Image ${idx + 1}`}
                              className="w-full h-full object-contain cursor-pointer"
                              crossOrigin="anonymous"
                              draggable={false}
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 cursor-pointer" />
                          )}
                          {ratioLabel ? (
                            <div className="absolute left-0 bottom-0 m-[2px] px-1 py-0.5 bg-black/80 text-white text-[10px] leading-none rounded">
                              {ratioLabel}
                            </div>
                          ) : null}
                          {/* Remove button (like FileAttachmentsPreview close) */}
                          {!isLast && (
                            <button
                              type="button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                handleRemoveImage(String(img.id));
                              }}
                              className="group-hover:opacity-100 opacity-0 absolute -top-[7px] -right-[7px] h-[14px] w-[14px] rounded-full bg-[#09090B80] shadow-sm flex items-center justify-center text-white transition-opacity"
                              title="Remove image"
                              aria-label="Remove image"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="12"
                                height="12"
                                fill="currentColor"
                              >
                                <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 1 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {/* Add Image Button with aspect ratio hover (anchored above) */}
                    <div
                      className="relative"
                      onMouseEnter={(e) => {
                        const btnRect = (
                          e.currentTarget as HTMLElement
                        ).getBoundingClientRect();
                        const prevRect = previewRef.current?.getBoundingClientRect();
                        if (!prevRect) return;
                        setAspectMenuPos({
                          left: btnRect.left - prevRect.left + btnRect.width / 2,
                          top: btnRect.top - prevRect.top,
                          width: btnRect.width,
                        });
                        cancelHideAspectMenu();
                        setShowAspectMenu(true);
                      }}
                      onMouseLeave={scheduleHideAspectMenu}
                    >
                      <button
                        className="w-12 h-12 rounded border border-dashed border-gray-300 hover:border-gray-400 bg-white flex items-center justify-center cursor-pointer"
                        aria-label="Add image"
                      >
                        <IconPlus className="w-5 h-5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
              {/* Anchored aspect ratio menu above button, same width, closes when not hovered */}
              {showAspectMenu && aspectMenuPos ? (
                <div
                  className="absolute z-[3000]"
                  style={{
                    left: `${aspectMenuPos.left}px`,
                    top: `${aspectMenuPos.top}px`,
                    transform: "translate(-50%, -100%)",
                  }}
                  onMouseEnter={cancelHideAspectMenu}
                  onMouseLeave={scheduleHideAspectMenu}
                >
                  <div
                    className="bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden py-1"
                    style={{ width: `${Math.max(aspectMenuPos?.width || 56, 76)}px` }}
                  >
                    {["9:16", "2:3", "4:5", "1:1", "3:2", "16:9"].map((ratio) => {
                      const isDisabled = existingAspectRatios.has(ratio);
                      const parts = ratio.split(":");
                      const rw = parseInt(parts[0] || "1", 10) || 1;
                      const rh = parseInt(parts[1] || "1", 10) || 1;
                      const base = 18;
                      const isWide = rw >= rh;
                      const boxW = isWide ? base : Math.round(base * (rw / rh));
                      const boxH = isWide ? Math.round(base * (rh / rw)) : base;
                      return (
                        <button
                          key={ratio}
                          className={`w-full px-2 py-1.5 text-xs flex items-center gap-2 ${isDisabled ? "text-gray-400 cursor-not-allowed opacity-60" : "text-gray-800 hover:bg-gray-50"}`}
                          disabled={isDisabled}
                          onClick={(e) => {
                            if (isDisabled) return;
                            e.stopPropagation();
                            setSelectedTextBox(null);
                            setDeselectToken((t) => t + 1);
                            setImageDeselectToken((t) => t + 1);
                            setShowAspectMenu(false);
                            // Immediately append a local placeholder so the new image button appears with loader
                            const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                            const placeholder: Image = {
                              id: tempId,
                              url: "",
                              hash: "",
                              width: 0,
                              height: 0,
                              status: "pending",
                              aspect_ratio: ratio,
                              permalink_url: "",
                              progress: "",
                              thumbnail_url: "",
                              creative_type: "image",
                            } as Image;
                            // Remember previously selected media index to restore on failure
                            previousActiveIndexRef.current = activeImageId;
                            // Append placeholder and compute index atomically to avoid race when adding multiple images quickly
                            setAppendedMedia((prev) => {
                              setActiveImageId(tempId);
                              return [...prev, placeholder];
                            });
                            // Include the new temp image in visibleIds immediately so history can capture it
                            setVisibleImageIds((prev) => {
                              const set = new Set(prev || []);
                              if (!set.has(tempId)) return [...(prev || []), tempId];
                              return prev;
                            });

                            // Fire request and then reconcile placeholder with server response; polling effect will handle updates
                            void (async () => {
                              try {
                                const accountId = String(
                                  useGlobalStore.getState().selectedAccount?.id || "",
                                );
                                if (!accountId || !adCreativeId) {
                                  setAppendedMedia((prev) =>
                                    prev.filter((im) => im.id !== tempId),
                                  );
                                  setActiveImageId(previousActiveIndexRef.current);
                                  return;
                                }
                                const res = await addImageToAdCreative(
                                  accountId,
                                  activeImage?.id || "",
                                  ratio,
                                  currentBackgroundImageEditByImageId[
                                    activeImage?.id || ""
                                  ]?.id || "", // image_edit_id - current background image edit ID
                                );
                                const detail: Image | null = res?.data ?? null;
                                if (detail) {
                                  // Replace temp placeholder with server-provided detail
                                  setAppendedMedia((prev) =>
                                    prev.map((im) => (im.id === tempId ? detail : im)),
                                  );
                                  // Update visibleIds set to swap tempId -> real id while preserving order
                                  setVisibleImageIds((prev) => {
                                    const ids = [...(prev || [])];
                                    const idx = ids.findIndex((v) => v === tempId);
                                    if (idx >= 0) ids[idx] = String(detail.id);
                                    else ids.push(String(detail.id));
                                    return ids;
                                  });
                                  setActiveImageId(String(detail.id));
                                  // If another placeholder with the same aspect ratio exists (due to rapid clicks), keep both;
                                  // de-duplication is handled against server list by id, so distinct requests remain visible.
                                  // Seed polled map to ensure UI picks up detail immediately
                                  setPolledMediaMap((prev) => ({
                                    ...prev,
                                    [detail.id]: detail,
                                  }));
                                  // Mark this id as session-added for delete-on-close semantics
                                  try {
                                    sessionAddedIdsRef.current.add(String(detail.id));
                                    setSessionAddedIdsState((prev) => {
                                      const next = new Set(prev);
                                      next.add(String(detail.id));
                                      return next;
                                    });
                                  } catch {}
                                  // Update dashboard creative (local state only)
                                  if (currentAdCreativeRef.current) {
                                    const baseCreative = currentAdCreativeRef.current;
                                    const currentImages =
                                      baseCreative.creative.images || [];
                                    const alreadyPresentById = currentImages.some(
                                      (im) => im.id === detail.id,
                                    );
                                    let updatedImages = currentImages;
                                    if (!alreadyPresentById) {
                                      updatedImages = [...currentImages, detail];
                                    }
                                    // Update local state only, actual saving happens on save button click
                                    currentAdCreativeRef.current = {
                                      ...baseCreative,
                                      creative: {
                                        ...baseCreative.creative,
                                        images: updatedImages,
                                      },
                                    };
                                  }
                                } else {
                                  // Remove temp on failure to get detail
                                  setAppendedMedia((prev) =>
                                    prev.filter((im) => im.id !== tempId),
                                  );
                                  setVisibleImageIds((prev) =>
                                    (prev || []).filter((v) => v !== tempId),
                                  );
                                  setActiveImageId(previousActiveIndexRef.current);
                                }
                              } catch (err) {
                                console.error(err);
                                // Remove temp on error
                                setAppendedMedia((prev) =>
                                  prev.filter((im) => im.id !== tempId),
                                );
                                setVisibleImageIds((prev) =>
                                  (prev || []).filter((v) => v !== tempId),
                                );
                                setActiveImageId(previousActiveIndexRef.current);
                              }
                            })();
                          }}
                        >
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm border border-gray-300">
                            <span
                              className={`block rounded-[2px] ${isDisabled ? "bg-gray-200" : "bg-gray-200"}`}
                              style={{ width: `${boxW}px`, height: `${boxH}px` }}
                            />
                          </span>
                          <span>{ratio}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Left overlay panel for font/color (positioned over second sidebar) */}
          {leftPanel && (
            <div className="absolute left-[80px] top-[57px] z-30 w-[250px] h-[calc(100%-57px)] border-r bg-white shadow-lg">
              <div className="flex items-center justify-between px-4 h-12 border-b">
                <div className="text-[16px] font-semibold text-[#0A0A0A]">
                  {leftPanel === "font" ? "Font" : "Text colour"}
                </div>
                <button
                  onClick={() => setLeftPanel(null)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-gray-100"
                >
                  <IconX className="w-4 h-4" />
                </button>
              </div>

              {leftPanel === "font" && (
                <div className="flex flex-col h-[calc(100%-48px)]">
                  <div className="p-3">
                    <div className="relative">
                      <input
                        value={fontSearch}
                        onChange={(e) => setFontSearch(e.target.value)}
                        onKeyDown={(e) => {
                          // Prevent the keydown event from bubbling up to parent components
                          // This stops the delete key from affecting text boxes when typing in search
                          e.stopPropagation();
                        }}
                        onFocus={(e) => {
                          // Ensure the input has focus and prevent event bubbling
                          e.stopPropagation();
                        }}
                        placeholder="Search fonts..."
                        className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm pl-9 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                        autoComplete="off"
                      />
                      <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 px-3">
                    <FontList
                      fonts={displayedFonts}
                      popularFonts={popularFonts}
                      selectedFont={selectedFont}
                      loadedFonts={loadedFonts}
                      visibleCount={visibleCount}
                      isCompact={false}
                      maxHeight="calc(100vh - 300px)"
                      onFontSelect={(font) => {
                        setSelectedFont(font);
                        if (selectedTextBox) applyFormatting({ fontFamily: font });
                      }}
                      onScroll={(e) => {
                        const el = e.currentTarget;
                        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
                          setVisibleCount((c) =>
                            Math.min(c + PAGE_SIZE, filteredGoogleFonts.length),
                          );
                        }
                      }}
                      onRequestFontFace={requestFontFace}
                      ensureGoogleFontLoaded={ensureGoogleFontLoaded}
                    />
                  </div>
                </div>
              )}

              {leftPanel === "color" && (
                <div className="p-3 overflow-auto h-[calc(100%-48px)]">
                  {/* Custom HEX input */}
                  <div className="mb-3 flex items-center gap-2">
                    <input
                      value={customColorValue}
                      onChange={(e) => setCustomColorValue(e.target.value)}
                      onKeyDown={(e) => {
                        // Prevent the keydown event from bubbling up to parent components
                        // This stops the delete key from affecting text boxes when typing in color input
                        e.stopPropagation();
                      }}
                      onFocus={(e) => {
                        // Ensure the input has focus and prevent event bubbling
                        e.stopPropagation();
                      }}
                      onBlur={() => {
                        const v = customColorValue.trim();
                        const normalized = v.startsWith("#") ? v : `#${v}`;
                        const valid = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(
                          normalized,
                        );
                        if (valid) {
                          setSelectedColor(normalized);
                          if (selectedTextBox) applyFormatting({ fill: normalized });
                        }
                      }}
                      placeholder="#000000"
                      className="flex-1 h-9 rounded border px-2 text-sm"
                      autoComplete="off"
                    />
                    <div className="relative">
                      <input
                        type="color"
                        value={selectedColor}
                        onChange={(e) => {
                          setSelectedColor(e.target.value);
                          setCustomColorValue(e.target.value.toUpperCase());
                          if (selectedTextBox) applyFormatting({ fill: e.target.value });
                        }}
                        className="absolute inset-0 w-9 h-9 opacity-0 cursor-pointer"
                      />
                      <div
                        className="w-9 h-9 rounded-full border border-gray-300 cursor-pointer hover:border-gray-400 transition-colors"
                        style={{ backgroundColor: selectedColor }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-8 gap-2">
                    {[
                      "#000000",
                      "#FFFFFF",
                      "#FF3B30",
                      "#FF9500",
                      "#FFCC00",
                      "#34C759",
                      "#007AFF",
                      "#AF52DE",
                      "#5856D6",
                      "#5AC8FA",
                      "#30B0C7",
                      "#FF375F",
                      "#8E8E93",
                      "#FF9F0A",
                      "#BF5AF2",
                      "#32D74B",
                    ].map((c) => (
                      <button
                        key={c}
                        className={`h-7 w-7 rounded-full border ${selectedColor === c ? "border-2 border-[#7C3AED]" : "border-border-gray"}`}
                        style={{ backgroundColor: c }}
                        onClick={() => {
                          setSelectedColor(c);
                          setCustomColorValue(c.toUpperCase());
                          if (selectedTextBox) applyFormatting({ fill: c });
                        }}
                        aria-label={`Pick ${c}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesDialog
        isOpen={showUnsavedChangesModal}
        onCancel={handleCancelClose}
        onDiscard={handleDiscardChanges}
        onSaveAndContinue={() => void handleSaveAndContinue()}
        container={editorContainerRef.current}
        title="Save your changes?"
        description="You've made some edits. Would you like to save before closing?"
        saveButtonText="Save & Close"
        discardButtonText="Discard Changes"
        cancelButtonText="Stay"
      />
    </Dialog>
  );
};

export default EditImageDialog;
